import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { BulkEmailChat } from '@/components/BulkEmailChat';
import { DebouncedFilterInput } from '@/components/DebouncedFilterInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Building2, Plus, Search, MapPin, Mail, Phone, Pencil, Trash2, FlaskConical, Upload, User, FileText, X, ChevronDown, ChevronUp, Globe, Send, Brain, Check, Download, Copy, Sparkles, Loader2, Share2, ShieldAlert, Star, AlertTriangle, History, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CountrySelect } from '@/components/CountrySelect';
import { Pagination } from '@/components/Pagination';
import { CompanySelect } from '@/components/CompanySelect';
import { uploadDocumentWithDedupe } from '../utils/uploadDocument';
import BulkSendProgress from '@/components/BulkSendProgress';
const API = '/api';

const STAGES = ['Pre-Clinical', 'IND', 'Phase 1', 'Phase 2', 'Phase 3', 'Clinical Stage', 'Commercial'];
const AGREEMENT_STATUSES = ['Not Started', 'Sent', 'Under Review', 'Finalised', 'Signed'];
const AGREEMENT_STATUS_COLORS = {
  'Not Started': 'bg-slate-100 text-slate-500',
  'Sent': 'bg-sky-100 text-sky-700',
  'Under Review': 'bg-amber-100 text-amber-700',
  'Finalised': 'bg-blue-100 text-blue-700',
  'Signed': 'bg-emerald-100 text-emerald-700',
};
const NCD_STATUSES = ['Not Received', 'Received', 'Shared with Pharma', 'Shared with Investors', 'Shared with Both'];
const STAGE_COLORS = {
  'Discovery': 'bg-zinc-100 text-zinc-700',
  'Pre-Clinical': 'bg-slate-100 text-slate-700',
  'IND': 'bg-sky-100 text-sky-700',
  'Phase 1': 'bg-blue-100 text-blue-700',
  'Phase 2': 'bg-violet-100 text-violet-700',
  'Phase 3': 'bg-purple-100 text-purple-700',
  'Clinical Stage': 'bg-fuchsia-100 text-fuchsia-700',
  'Commercial': 'bg-emerald-100 text-emerald-700',
  'Platform': 'bg-amber-100 text-amber-700',
};

const STATUSES = ['Pipeline', 'Active', 'In Negotiation', 'On Hold', 'Closed', 'Dropped'];
const EMAILING_STATUSES = ['Not Started', 'In Progress', 'Completed'];
const INACTIVE_STATUSES = ['Ghosted', 'Deal Closed', 'Deal Lost', 'Declined'];
const OUTREACH_STATUSES = ['Introduction Made', 'Email Acknowledged', 'Interest Shown', 'Asset Under Evaluation', 'Meeting Requested', 'Meeting Scheduled', 'CDA in Progress', 'CDA Signed', 'Awaiting Trial Data', 'Due Diligence', 'Deal Discussion', 'On Hold', 'Deal Closed', 'Deal Lost', 'Ghosted', 'Declined'];
const STAGE_ORDER = { 'Commercial': 0, 'Phase 3': 1, 'Phase 2': 2, 'Phase 1': 3, 'Clinical Stage': 4, 'IND': 5, 'Pre-Clinical': 6, 'Discovery': 7, 'Platform': 8 };
const sortByStage = (a, b) => (STAGE_ORDER[a.stage] ?? 9) - (STAGE_ORDER[b.stage] ?? 9);
const STATUS_COLORS = {
  'Pipeline': 'bg-slate-100 text-slate-600',
  'Active': 'bg-sky-100 text-sky-700',
  'Introduction Made': 'bg-cyan-100 text-cyan-700',
  'Email Acknowledged': 'bg-lime-100 text-lime-700',
  'In Negotiation': 'bg-purple-100 text-purple-700',
  'On Hold': 'bg-amber-100 text-amber-700',
  'Closed': 'bg-green-100 text-green-700',
  'Dropped': 'bg-rose-100 text-rose-700',
  'Asset Under Evaluation': 'bg-amber-100 text-amber-700',
  'Meeting Requested': 'bg-violet-50 text-violet-600',
  'Meeting Scheduled': 'bg-violet-100 text-violet-700',
  'CDA in Progress': 'bg-blue-50 text-blue-600',
  'CDA Signed': 'bg-blue-100 text-blue-700',
  'Awaiting Trial Data': 'bg-orange-100 text-orange-700',
  'Due Diligence': 'bg-indigo-100 text-indigo-700',
  'Deal Discussion': 'bg-purple-100 text-purple-700',
  'On Hold': 'bg-amber-100 text-amber-700',
  'Deal Closed': 'bg-green-100 text-green-700',
  'Deal Lost': 'bg-rose-100 text-rose-700',
  'Ghosted': 'bg-orange-100 text-orange-700',
  'Declined': 'bg-rose-100 text-rose-700',
  'Not Started': 'bg-slate-100 text-slate-600',
  'In Progress': 'bg-sky-100 text-sky-700',
  'Completed': 'bg-emerald-100 text-emerald-700',
};

const emptyContact = { name: '', designation: '', email: '', phone: '' };
const PARAMETER_FIELDS = ['Molecule/Product Number', 'Platform', 'Target', 'Modality', 'Indication', 'Drug Delivery Mechanism'];
const emptyMolecule = { 'Molecule/Product Number': '', 'Platform': '', 'Target': '', 'Modality': '', 'Indication': '', 'Drug Delivery Mechanism': '', stage: 'Pre-Clinical', stage_notes: '', molecule_description: '', objectives: '', deal_interests: [], emailing_status: 'Not Started', status: 'Pipeline' };

const DEAL_INTEREST_OPTIONS = [
  { value: 'Out-Licensing', label: 'Out-Licensing', color: 'bg-sky-100 text-sky-700' },
  { value: 'Co-Development & R&D', label: 'Co-Dev & R&D', color: 'bg-blue-100 text-blue-700' },
  { value: 'Investment / Fundraising', label: 'Fundraising', color: 'bg-violet-100 text-violet-700' },
];

const emptyForm = {
  company_name: '', location: '', website: '', contacts: [{ ...emptyContact }],
  molecules: [{ ...emptyMolecule }], stage: 'Pre-Clinical', stage_notes: '', therapeutic_area: '',
  company_description: '', molecule_description: '', objectives: '',
  agreement_status: 'Not Started', cda_status: 'Not Started', partnership_status: 'Not Started', ncd_status: 'Not Received', company_status: 'Not Started',
  deal_interests: [],
  target_companies: [],
  funding_stage: '', funding_amount: '', use_of_funds: '',
};

// (OutLicenserCategoryChips was promoted to the top SubTabBar — see
// `SUB_TABS.biotechs` in `components/navConfig.js`. Inline chips removed.)

export default function BiotechCompaniesPage({ category: propCategory = '', pageTitle = '' }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Allow the URL to override the prop-provided category so the unified
  // /biotech (Out-Licensers) route can switch between [All · Biotech ·
  // NDDS · DTx · Platform · Academic] via filter chips without separate
  // routes. When the page is invoked via a dedicated route with a
  // hardcoded `category` prop (legacy specialty routes), the prop wins.
  const category = (propCategory || searchParams.get('category') || '').trim();
  const [activeFilter, setActiveFilter] = useState(searchParams.get('filter') === 'active');
  const [interestFilters, setInterestFilters] = useState(() => {
    const f = searchParams.get('filter');
    if (f === 'fundraising') return new Set(['Investment / Fundraising']);
    if (f === 'out-licensing') return new Set(['Out-Licensing']);
    if (f === 'co-dev') return new Set(['Co-Development & R&D']);
    return new Set();
  });
  const [stageFilter, setStageFilter] = useState(searchParams.get('stage') || '');
  const [minStageFilter, setMinStageFilter] = useState(searchParams.get('min_stage') || '');
  // Multi-select stages (OR logic) — replaces `min_stage`'s single
  // "everything at-or-above X" model. User can now say "Phase 2 OR
  // Phase 3 OR Filed" for cohort blast segmentation. Stored as a comma
  // list in the URL for shareable filtered views.
  const [stagesFilter, setStagesFilter] = useState(() => {
    const s = (searchParams.get('stages') || '').trim();
    return s ? s.split(',').filter(Boolean) : [];
  });
  const [stagesPopoverOpen, setStagesPopoverOpen] = useState(false);
  // Advanced Filters collapsible — hides TA / Modality / Exclude-Met
  // behind a chevron so the primary toolbar stays campaign-focused
  // (Cohort · Stage · DNC · Pending · Ready-to-email · Select All).
  const [advancedOpen, setAdvancedOpen] = useState(() =>
    !!(searchParams.get('ta') || searchParams.get('modality') || searchParams.get('cohort_excl_met') === '1')
  );
  // Free-text asset filters — substring match on asset.therapeutic_area /
  // indication / target (TA) or asset.modality / platform / drug_delivery
  // (Modality). Used to narrow a large cohort blast down to a specific
  // therapeutic focus (e.g. "oncology" + "ADC" → tight, high-signal batch).
  const [taFilter, setTaFilter] = useState(searchParams.get('ta') || '');
  const [modalityFilter, setModalityFilter] = useState(searchParams.get('modality') || '');
  const [orphanFilter, setOrphanFilter] = useState(false);
  const [orphanCount, setOrphanCount] = useState(0);
  const [agreementFilter, setAgreementFilter] = useState('');
  const [emailingFilter, setEmailingFilter] = useState('');
  const [dncFilter, setDncFilter] = useState(searchParams.get('filter') === 'dnc');
  // Pending Outreach filter — biotechs where we've been given freehand to
  // run a campaign but haven't started yet (at least one asset stuck at
  // "Not Started", at least one contact email on file, not DNC). These
  // are the next-action queue for the BD team.
  const [pendingOutreachFilter, setPendingOutreachFilter] = useState(searchParams.get('filter') === 'pending_outreach');
  // Email-presence filter — 3-state pill (`all` / `has` / `none`).
  // - `has`  → only biotechs with at least one valid email (top-level
  //            contact_email/email OR any contacts[].email). Use this for
  //            shareable exports (e.g. the Viatris hand-off).
  // - `none` → the inverse: biotechs we cannot reach by email and that
  //            therefore are good enrichment / web-research candidates.
  // Stored in the URL as ?email=has|none so the filtered list is
  // shareable and persists across reloads.
  const [emailFilter, setEmailFilter] = useState(() => {
    const e = (searchParams.get('email') || '').toLowerCase();
    return e === 'has' || e === 'none' ? e : 'all';
  });
  // Conference cohort filter — limits the table to biotechs that came in
  // from a specific conference import (e.g. BioChina 2026). With optional
  // "exclude Met In-Person" toggle for outreach to people the user hasn't
  // already met face-to-face.
  const [cohortFilter, setCohortFilter] = useState(searchParams.get('cohort') || '');
  const [cohortExcludeMet, setCohortExcludeMet] = useState(searchParams.get('cohort_excl_met') === '1');
  const [cohortIds, setCohortIds] = useState(null); // null = inactive, Set = active
  const [cohortMeta, setCohortMeta] = useState(null);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [availableCohorts, setAvailableCohorts] = useState([]);  // dynamic dropdown source

  // Fetch the live list of cohort tags + counts so the dropdown self-populates.
  // Solves the bug where hardcoded options ("bio_international") never matched
  // actual import_tag values ("bio_int_2026") and silently returned 0 rows.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/cohorts`, { withCredentials: true });
        if (!cancelled) setAvailableCohorts(r.data?.items || []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const renameCohort = async (oldTag) => {
    const current = availableCohorts.find(c => c.tag === oldTag);
    const suggested = current?.label || oldTag;
    const newTag = window.prompt(
      `Rename / merge cohort "${current?.label || oldTag}" (${current?.biotech_count || 0} biotechs).\n\n` +
      `Enter a target tag — records with this tag stay separate, OR pick an existing tag below to MERGE into it:\n\n` +
      availableCohorts.map(c => `  • ${c.tag}  →  ${c.label} (${c.biotech_count})`).join('\n') +
      '\n\nEnter the target tag exactly (e.g. bio_int_2026):',
      suggested.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
    );
    if (!newTag || newTag === oldTag) return;
    if (!window.confirm(`Move all biotechs/pharma/investors tagged "${oldTag}" into "${newTag}"?\n\nThis is non-destructive — records are just retagged. Cannot be undone via UI (would need to repeat the rename).`)) return;
    try {
      const r = await axios.post(
        `${API}/cohorts/${encodeURIComponent(oldTag)}/rename`,
        { new_tag: newTag },
        { withCredentials: true }
      );
      toast.success(`Renamed ${r.data.total} records · ${oldTag} → ${newTag}`);
      // Refresh dropdown
      const r2 = await axios.get(`${API}/cohorts`, { withCredentials: true });
      setAvailableCohorts(r2.data?.items || []);
      // If they were currently filtered by the old tag, update to new
      if (cohortFilter === oldTag) setCohortFilter(newTag);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    }
  };
  const [sourceFilter, setSourceFilter] = useState(() => {
    // Allow dashboard's "My Companies" card to deep-link onto the starred tab.
    const f = searchParams.get('filter');
    if (f === 'starred' || f === 'manual' || f === 'my-companies') return 'manual';
    if (f === 'database') return 'bio_international';
    return 'manual';
  }); // 'all', 'manual', 'bio_international'
  const [extractionStatus, setExtractionStatus] = useState(null);
  const extractionPollRef = useRef(null);
  const [companies, setCompanies] = useState([]);
  // Backend-computed full id list matching every applied filter — powers
  // the "Select all N" button so bulk selection never depends on how
  // many rows rendered in the visible table. Null when the backend
  // didn't return it (non-slim / non-cohort fetches).
  const [allMatchingIds, setAllMatchingIds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sourceCounts, setSourceCounts] = useState({ manual: 0, database: 0, all: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const searchTimeoutRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [uploading, setUploading] = useState(false);
  const [companyDocs, setCompanyDocs] = useState([]);
  const [docSlots, setDocSlots] = useState([{ type: 'Non-Confidential Deck', file: null, fileName: '' }]);
  const fileInputRefs = [useRef(null), useRef(null), useRef(null)];

  // Bulk conference .docx enrichment
  const bulkDocxInputRef = useRef(null);
  const [bulkEnrichRunning, setBulkEnrichRunning] = useState(false);  const [bulkEnrichResult, setBulkEnrichResult] = useState(null); // summary object from the API
  const [bulkEnrichLastRun, setBulkEnrichLastRun] = useState(null); // {file, enriched, new, assets, time} sticks after completion

  // ── Bulk Perplexity enrichment (web-research every thin biotech) ────────
  const [pplxEnrich, setPplxEnrich] = useState(null);  // { total, qualifying, job }
  const [pplxKicking, setPplxKicking] = useState(false);
  const pplxTickingRef = React.useRef(false);
  // Track the previous job status so we can detect the running → done edge
  // and fire a success toast + refresh the visible table. Without this the
  // user has no idea if their "Enrich 21 more" actually finished — the
  // button just silently changes label back to a fresh-looking "Enrich N"
  // even though N is now smaller. Feb 2026 · user report: "the button
  // continues to display 'Enrich 21 more' even after the operation
  // appears to finish".
  const pplxPrevStatusRef = React.useRef(null);
  // Preserves the just-finished job's enriched/failed counts so we can
  // render "✅ 18 enriched · ⚠️ 3 failed" even after the /preview endpoint
  // clears the running job. Cleared when the user starts a new run or
  // dismisses the pill.
  const [pplxLastResult, setPplxLastResult] = useState(null); // {enriched, failed, at}

  const loadPplxEnrich = async () => {
    try {
      const r = await axios.get(`${API}/biotech-enrichment/preview`, { withCredentials: true });
      setPplxEnrich(r.data);
      return r.data;
    } catch { /* non-fatal */ }
    return null;
  };

  const tickPplxOnce = async () => {
    try {
      const r = await axios.post(`${API}/biotech-enrichment/tick`, {}, {
        withCredentials: true,
        timeout: 60000, // one chunk = up to ~45s server-side
      });
      // Refresh preview so qualifying + breakdown counts stay in sync.
      await loadPplxEnrich();
      return r.data;
    } catch (err) {
      // Network blip / backend restart — surface but keep the loop going.
      // The next tick will pick up whatever the persisted job doc says.
      console.warn('[pplx-enrich] tick failed:', err?.message);
      return null;
    }
  };

  // Drive the tick loop client-side while the job is running. Each tick is
  // short (<45s) so it survives any backend restart. If the tab is closed
  // or the container recycles mid-tick, the next fresh session resumes
  // from where it left off because already-enriched biotechs are skipped.
  useEffect(() => {
    const st = pplxEnrich?.job?.status;
    if (st !== 'running' && st !== 'queued') {
      pplxTickingRef.current = false;
      return;
    }
    if (pplxTickingRef.current) return;
    pplxTickingRef.current = true;

    let cancelled = false;
    (async () => {
      while (!cancelled) {
        const res = await tickPplxOnce();
        if (cancelled) break;
        const nextStatus = res?.status;
        if (nextStatus && nextStatus !== 'running' && nextStatus !== 'queued') {
          // done / cancelled / error / idle — stop looping
          break;
        }
        // Small breather between ticks; the tick itself is up to ~45s.
        await new Promise(r => setTimeout(r, 1000));
      }
      pplxTickingRef.current = false;
    })();

    return () => { cancelled = true; pplxTickingRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pplxEnrich?.job?.status]);

  useEffect(() => { loadPplxEnrich(); }, []);

  // ── Completion detector — when the job flips from running/queued
  //    into a terminal state (done / cancelled / error), fire the
  //    right toast and refresh the visible table so the newly-enriched
  //    rows show fresh data. Also stash the final counts in
  //    `pplxLastResult` so the UI can display "✅ N enriched · ⚠ M
  //    failed" until dismissed. Feb 2026 · user report: "no clear
  //    indication that the enrichment has actually started, completed,
  //    or failed." ────────────────────────────────────────────────
  useEffect(() => {
    const prev = pplxPrevStatusRef.current;
    const curr = pplxEnrich?.job?.status || null;
    // Same status → nothing to notify.
    if (prev === curr) return;
    pplxPrevStatusRef.current = curr;
    // Only care about the transition FROM running/queued INTO a
    // terminal state. Ignore the first-load nulls / idle boot-up.
    if (prev !== 'running' && prev !== 'queued') return;
    const j = pplxEnrich?.job || {};
    const enriched = j.enriched || 0;
    const failed = j.failed || 0;
    if (curr === 'done') {
      // Success — or partial success if any items failed.
      if (failed === 0) {
        toast.success(`✅ Successfully enriched ${enriched} biotech ${enriched === 1 ? 'company' : 'companies'}`);
      } else {
        toast.success(`✅ ${enriched} enriched · ⚠️ ${failed} failed`, { duration: 6000 });
      }
      setPplxLastResult({ enriched, failed, at: Date.now() });
      // Refresh the visible table so freshly-enriched fields render.
      fetchCompanies();
    } else if (curr === 'cancelled') {
      toast(`Enrichment cancelled — ${enriched} were processed before stopping`, { icon: '⏸️' });
      setPplxLastResult({ enriched, failed, at: Date.now(), cancelled: true });
      fetchCompanies();
    } else if (curr === 'error') {
      toast.error(`❌ Enrichment failed: ${j.error || 'unknown error'}. Click the button to retry.`);
      setPplxLastResult({ enriched, failed, at: Date.now(), error: j.error || 'failed' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pplxEnrich?.job?.status]);

  const kickPplxEnrich = async () => {
    if (!window.confirm(
      `Run Perplexity web-research on ${pplxEnrich?.qualifying || 0} biotechs missing structural data?\n\n` +
      `Each biotech gets one live web search to fill: website, description, headquarters, founded year, ` +
      `therapeutic area, modality, CEO, key differentiators, recent milestones. ` +
      `Only EMPTY fields are filled — your existing data is never overwritten.\n\n` +
      `Processed in short chunks driven from your browser (safe across backend restarts). ` +
      `Estimated cost ≈ $${((pplxEnrich?.qualifying || 0) * 0.008).toFixed(2)} at ~$0.008 / biotech.\n\n` +
      `Keep this tab open until it finishes — closing it pauses progress ` +
      `(you can resume any time by clicking the button again).`
    )) return;
    setPplxKicking(true);
    // Optimistically flip the button into "Queued…" state instantly so
    // the user gets immediate feedback that their click was registered.
    // The tick loop takes over as soon as /run's response updates the
    // real job doc.
    setPplxEnrich(prev => prev ? { ...prev, job: { ...(prev.job || {}), status: 'queued', enriched: 0, failed: 0, total: prev.qualifying || 0 } } : prev);
    setPplxLastResult(null); // dismiss any prior result banner
    try {
      const r = await axios.post(`${API}/biotech-enrichment/run`, {}, { withCredentials: true });
      if (r.data?.started) {
        toast.success(`Enrichment queued for ${r.data.qualifying} biotechs — keep this tab open`);
        loadPplxEnrich();
      } else {
        toast.info(r.data?.reason || 'Nothing to enrich');
        // Rollback the optimistic queued state.
        setPplxEnrich(prev => prev ? { ...prev, job: null } : prev);
      }
    } catch (e) {
      // Rollback the optimistic queued state on failure.
      setPplxEnrich(prev => prev ? { ...prev, job: null } : prev);
      toast.error(e?.response?.data?.detail || 'Could not start enrichment');
    } finally {
      setPplxKicking(false);
    }
  };

  const cancelPplxEnrich = async () => {
    try {
      await axios.post(`${API}/biotech-enrichment/cancel`, {}, { withCredentials: true });
      toast.success('Cancel requested — current in-flight calls will finish then stop.');
      loadPplxEnrich();
    } catch { toast.error('Cancel failed'); }
  };

  const resetPplxEnrich = async () => {
    if (!window.confirm(
      'Force-reset the enrichment job state?\n\n' +
      'Use this if the button is stuck showing a spinner (worker died in a backend restart). ' +
      'Already-enriched biotechs will still be skipped — no data is lost.'
    )) return;
    try {
      await axios.post(`${API}/biotech-enrichment/reset`, {}, { withCredentials: true });
      toast.success('Enrichment job state cleared — you can kick off a fresh run.');
      loadPplxEnrich();
    } catch { toast.error('Reset failed'); }
  };

  // Duplicate detection (inline banner + expandable panel).
  // Jul 2026 · user request: the chronic red "potential duplicates detected"
  // banner is disabled. Flip this flag to bring the review/merge panel back.
  const SHOW_DUP_BANNER = false;
  const [dupPairs, setDupPairs] = useState([]);
  const [dupTotal, setDupTotal] = useState(0);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupMergedIds, setDupMergedIds] = useState(new Set());
  const [dupMerging, setDupMerging] = useState(null); // pair key currently merging
  const [dupConfirm, setDupConfirm] = useState(null); // {pair, survivor, drop}
  const [dupDismissConfirm, setDupDismissConfirm] = useState(null); // {pair, pairKey}
  const [dupAutoMerged, setDupAutoMerged] = useState([]); // server-side auto-merged pairs from last fetch
  const [dupSelected, setDupSelected] = useState(new Set()); // pairKeys selected for bulk action
  const [dupBulkRunning, setDupBulkRunning] = useState(false);
  const [dupBulkMergeConfirm, setDupBulkMergeConfirm] = useState(false);
  const [dupBulkDismissConfirm, setDupBulkDismissConfirm] = useState(false);

  const fetchDuplicates = async () => {
    if (!SHOW_DUP_BANNER) return;
    setDupLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/duplicate-biotechs`, {
        params: { threshold: 88, limit: 100 },
        withCredentials: true,
      });
      setDupPairs(data.pairs || []);
      setDupTotal(data.total_pairs || 0);
      setDupAutoMerged(data.auto_merged || []);
      if ((data.auto_merged || []).length > 0) {
        toast.success(`Auto-merged ${data.auto_merged.length} pair(s) sharing email domains`);
      }
    } catch {
      // silent — banner just won't appear
    } finally {
      setDupLoading(false);
    }
  };

  useEffect(() => { fetchDuplicates(); /* eslint-disable-line */ }, []);

  // Refetch the company list whenever the user clicks a top sub-tab pill
  // (Biotech Companies / Formulation / DTx / Platform / Academic). The
  // pill updates `?category=...` in the URL — `searchParams` is reactive,
  // so this effect fires and pulls the filtered subset from /api/biotech-companies.
  // `fetchCompanies` handles the "My Companies has 0 in this specialty →
  // auto-switch to All" fallback internally, so users always see data on
  // click instead of a confusing empty table.
  useEffect(() => {
    fetchCompanies(1, search, sourceFilter, stageFilter, orphanFilter, emailFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const handleMerge = async (survivorId, dropId, pairKey) => {
    setDupMerging(pairKey);
    try {
      const { data } = await axios.post(`${API}/admin/merge-biotech-companies`, {
        survivor_id: survivorId, drop_id: dropId,
      }, { withCredentials: true });
      const movedStr = Object.entries(data.collections_moved || {})
        .filter(([, v]) => typeof v === 'number' && v > 0)
        .map(([k, v]) => `${v} ${k}`).join(', ');
      toast.success(`Merged · +${data.contacts_added || 0} contacts${movedStr ? ' · moved ' + movedStr : ''}`);
      setDupMergedIds(prev => new Set([...prev, pairKey]));
      setDupConfirm(null);
      try { fetchCompanies(); } catch { /* silent */ }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Merge failed';
      toast.error(detail);
      // 404 → the pair is stale (record vanished, likely merged in another
      // session). Auto-refresh the duplicate list so the phantom pair
      // disappears and the user can resume cleanup without manual reload.
      if (status === 404) {
        setDupConfirm(null);
        setDupPairs(prev => prev.filter(p => `${p.a.id}::${p.b.id}` !== pairKey));
        setDupTotal(t => Math.max(0, t - 1));
        try { fetchDuplicates(); } catch { /* silent */ }
      }
    } finally {
      setDupMerging(null);
    }
  };

  const handleDismissDuplicate = async (aId, bId, pairKey) => {
    setDupMerging(pairKey); // reuse spinner state to disable the row
    try {
      await axios.post(`${API}/admin/duplicate-biotechs/dismiss`,
        { a_id: aId, b_id: bId },
        { withCredentials: true }
      );
      toast.success('Marked as not a duplicate — won\'t show again');
      // Remove pair from local state so it disappears immediately.
      // Match by the locally-built key (sorted is irrelevant — we built
      // the same key earlier and passed it in as pairKey).
      setDupPairs(prev => prev.filter(p => `${p.a.id}::${p.b.id}` !== pairKey));
      setDupTotal(t => Math.max(0, t - 1));
      setDupConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dismiss failed');
    } finally {
      setDupMerging(null);
    }
  };

  const toggleDupSelected = (pairKey) => {
    setDupSelected(prev => {
      const next = new Set(prev);
      if (next.has(pairKey)) next.delete(pairKey); else next.add(pairKey);
      return next;
    });
  };

  const toggleDupSelectAll = () => {
    setDupSelected(prev => {
      const allKeys = dupPairs
        .map(p => `${p.a.id}::${p.b.id}`)
        .filter(k => !dupMergedIds.has(k));
      if (prev.size === allKeys.length) return new Set();
      return new Set(allKeys);
    });
  };

  const handleBulkMerge = async () => {
    if (dupSelected.size === 0) return;
    const pairs = dupPairs
      .filter(p => dupSelected.has(`${p.a.id}::${p.b.id}`))
      .map(p => ({ a_id: p.a.id, b_id: p.b.id }));
    setDupBulkRunning(true);
    setDupBulkMergeConfirm(false);
    try {
      const { data } = await axios.post(
        `${API}/admin/duplicate-biotechs/bulk-merge`,
        { pairs },
        { withCredentials: true },
      );
      const merged = data.merged_count || 0;
      const failed = data.failed_count || 0;
      toast.success(`Merged ${merged}${failed ? ` · ${failed} failed` : ''}`);
      // Mark all successfully-merged pairKeys as done and refresh.
      const mergedDropIds = new Set((data.merged || []).map(m => m.dropped_id));
      setDupMergedIds(prev => {
        const next = new Set(prev);
        dupPairs.forEach(p => {
          if (mergedDropIds.has(p.a.id) || mergedDropIds.has(p.b.id)) {
            next.add(`${p.a.id}::${p.b.id}`);
          }
        });
        return next;
      });
      setDupSelected(new Set());
      try { fetchCompanies(); } catch { /* silent */ }
      try { fetchDuplicates(); } catch { /* silent */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk merge failed');
    } finally {
      setDupBulkRunning(false);
    }
  };

  const handleBulkDismiss = async () => {
    if (dupSelected.size === 0) return;
    const pairs = dupPairs
      .filter(p => dupSelected.has(`${p.a.id}::${p.b.id}`))
      .map(p => ({ a_id: p.a.id, b_id: p.b.id }));
    setDupBulkRunning(true);
    setDupBulkDismissConfirm(false);
    try {
      const { data } = await axios.post(
        `${API}/admin/duplicate-biotechs/bulk-dismiss`,
        { pairs },
        { withCredentials: true },
      );
      toast.success(`Marked ${data.dismissed || 0} pair(s) as not duplicates`);
      const dismissedKeys = new Set(pairs.map(p => `${p.a_id}::${p.b_id}`));
      setDupPairs(prev => prev.filter(p => !dismissedKeys.has(`${p.a.id}::${p.b.id}`)));
      setDupTotal(t => Math.max(0, t - (data.dismissed || 0)));
      setDupSelected(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk dismiss failed');
    } finally {
      setDupBulkRunning(false);
    }
  };

  const handleDismissAll = async () => {
    if (dupPairs.length === 0) return;
    if (!window.confirm(
      `Mark all ${dupPairs.length} detected pair(s) as NOT duplicates?\n\n` +
      `They will be remembered and won't reappear in the banner. ` +
      `Only NEW pairs from future imports will show up here again.`
    )) return;
    const pairs = dupPairs
      .filter(p => !dupMergedIds.has(`${p.a.id}::${p.b.id}`))
      .map(p => ({ a_id: p.a.id, b_id: p.b.id }));
    setDupBulkRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/duplicate-biotechs/bulk-dismiss`,
        { pairs },
        { withCredentials: true },
      );
      toast.success(`Dismissed all ${data.dismissed || 0} pair(s) — banner cleared`);
      setDupPairs([]);
      setDupTotal(0);
      setDupSelected(new Set());
      setDupOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dismiss-all failed');
    } finally {
      setDupBulkRunning(false);
    }
  };


  const handleBulkDocxChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBulkEnrichRunning(true);
    setBulkEnrichResult(null);
    const toastId = toast.loading(`Uploading ${f.name}…`);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data: kickoff } = await axios.post(`${API}/imports/bulk-biotech-docx`, fd, {
        withCredentials: true,
        timeout: 300_000,  // 5 min — large docs can take time to upload + initial-parse
      });
      const jobId = kickoff.job_id;
      if (!jobId) throw new Error('No job_id returned');

      toast.message('Parsing with Claude Sonnet 4.5…', { id: toastId, description: 'This usually takes 60–120s for a 20-company doc.' });

      // Poll every 3 seconds
      const start = Date.now();
      let final = null;
      let pollErrors = 0;
      let lastUpdateAt = Date.now();
      while (Date.now() - start < 15 * 60_000) {
        await new Promise(r => setTimeout(r, 3000));
        let job;
        try {
          const { data } = await axios.get(`${API}/imports/bulk-biotech-docx/${jobId}`, { withCredentials: true });
          job = data;
          pollErrors = 0;
        } catch (err) {
          pollErrors += 1;
          toast.message(
            'Parsing — connection hiccup',
            { id: toastId, description: `Re-checking in 3s (attempt ${pollErrors})…` },
          );
          continue;
        }
        if (job.stage === 'queued') {
          // Surface the queued state so the toast doesn't sit on the
          // initial "Parsing with Claude Sonnet 4.5…" forever when a
          // background worker is slow to start.
          const queuedSec = Math.floor((Date.now() - start) / 1000);
          toast.message(
            'Queued — waiting for worker',
            { id: toastId, description: queuedSec > 30 ? `Held in queue for ${queuedSec}s — server may be under load. Will keep retrying.` : 'Background worker picking up the job…' },
          );
        } else if (job.stage === 'matching' || job.stage === 'parsing') {
          lastUpdateAt = Date.now();
          const p = job.progress || 0;
          const s = job.summary || {};
          let desc;
          if (job.stage === 'parsing') {
            if (job.parse_chunks_total) {
              desc = `LLM parsing chunk ${job.parse_chunks_done || 0}/${job.parse_chunks_total} · ${job.parse_companies_found || 0} companies found`;
            } else {
              desc = 'LLM working… (chunk count incoming)';
            }
          } else {
            desc = `${s.enriched_existing || 0} enriched · ${s.created_new || 0} new so far…`;
          }
          toast.message(
            `${job.stage === 'parsing' ? 'Parsing' : 'Enriching'} — ${p}%`,
            { id: toastId, description: desc }
          );
        } else if (job.stage === 'done') {
          final = job;
          break;
        } else if (job.stage === 'failed') {
          throw new Error(job.error || 'Enrichment failed');
        }
        // Stuck-job warning: 4+ minutes without progress in parsing/matching
        if ((job.stage === 'parsing' || job.stage === 'matching') &&
            Date.now() - lastUpdateAt > 4 * 60_000) {
          const stuckMin = Math.floor((Date.now() - lastUpdateAt) / 60_000);
          toast.warning(
            `Stuck for ${stuckMin}+ min on ${job.stage}`,
            { id: toastId, description: 'Open the import history (clock icon) to cancel and retry.' },
          );
        }
      }
      if (!final) throw new Error('Enrichment timed out after 15 minutes');

      const sum = final.summary || {};
      toast.dismiss(toastId);
      toast.success(`${sum.enriched_existing || 0} enriched · ${sum.created_new || 0} new · ${sum.assets_added || 0} assets added`);
      setBulkEnrichResult(sum);
      setBulkEnrichLastRun({
        file: f.name,
        enriched: sum.enriched_existing || 0,
        created: sum.created_new || 0,
        assets: sum.assets_added || 0,
        contacts: sum.contacts_added || 0,
        at: Date.now(),
      });
      try { fetchCompanies(); } catch { /* silent */ }
      fetchDuplicates(); // refresh duplicate banner — imports may have created new pairs
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err?.response?.data?.detail || err?.message || 'Bulk enrichment failed');
    } finally {
      setBulkEnrichRunning(false);
      if (bulkDocxInputRef.current) bulkDocxInputRef.current.value = '';
    }
  };

  // Import History dialog state
  const [importHistoryOpen, setImportHistoryOpen] = useState(false);
  const [importHistoryJobs, setImportHistoryJobs] = useState([]);
  const [importHistoryLoading, setImportHistoryLoading] = useState(false);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [jobDetailsCache, setJobDetailsCache] = useState({}); // job_id -> full job with details[]

  const fetchImportHistory = async () => {
    setImportHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/imports/bulk-biotech-docx`, { withCredentials: true });
      setImportHistoryJobs(data?.jobs || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load import history');
    } finally {
      setImportHistoryLoading(false);
    }
  };

  const toggleJobDetails = async (jobId) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      return;
    }
    setExpandedJobId(jobId);
    if (!jobDetailsCache[jobId]) {
      try {
        const { data } = await axios.get(`${API}/imports/bulk-biotech-docx/${jobId}`, { withCredentials: true });
        setJobDetailsCache(prev => ({ ...prev, [jobId]: data }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load job details');
      }
    }
  };

  const cancelImportJob = async (jobId) => {
    setCancellingJobId(jobId);
    try {
      await axios.post(`${API}/imports/bulk-biotech-docx/${jobId}/cancel`, {}, { withCredentials: true });
      toast.success('Job cancelled');
      fetchImportHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cancel failed');
    } finally {
      setCancellingJobId(null);
    }
  };

  // Outreach state
  const [outreachMap, setOutreachMap] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  // CDMO Scanner state
  const [cdmoScanOpen, setCdmoScanOpen] = useState(false);
  const [cdmoScanning, setCdmoScanning] = useState(false);
  const [cdmoFlagged, setCdmoFlagged] = useState([]);
  const [cdmoSelected, setCdmoSelected] = useState(new Set());
  const [cdmoRemoving, setCdmoRemoving] = useState(false);
  const [deleteOutreachTarget, setDeleteOutreachTarget] = useState(null);
  const [deleteDocTarget, setDeleteDocTarget] = useState(null);
  const [outreachDialogOpen, setOutreachDialogOpen] = useState(false);
  const [outreachCompanyId, setOutreachCompanyId] = useState(null);
  const [editingOutreach, setEditingOutreach] = useState(null);

  // AI Portfolio Match state
  const [aiMatchQuery, setAiMatchQuery] = useState('');
  const [aiMatchResult, setAiMatchResult] = useState('');
  const [aiMatchLoading, setAiMatchLoading] = useState(false);
  const [aiMatchOpen, setAiMatchOpen] = useState(false);
  const [aiMatchType, setAiMatchType] = useState('pharma'); // 'pharma' or 'investor'
  const [aiSelectedMatches, setAiSelectedMatches] = useState(new Set()); // selected company_ids from match results
  const [aiStageFilter, setAiStageFilter] = useState([]); // multi-select stage filter
  const emptyOutreachForm = { biotech_company_id: '', asset_id: '', asset_name: '', contact_name: '', contact_email: '', company_name: '', country: '', designation: '', contact_type: 'Pharma', status: 'Asset Under Evaluation', notes: '', source_id: '', status_notes: '' };
  const [outreachForm, setOutreachForm] = useState({ ...emptyOutreachForm });
  // Multi-select bucket for "From Existing Contacts" mode — lets the user
  // tag multiple pharma/investor contacts to the biotech in one go.
  const [outreachSelectedIds, setOutreachSelectedIds] = useState([]);
  const [pharmaContacts, setPharmaContacts] = useState([]);
  const [investorContacts, setInvestorContacts] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [biotechContactsList, setBiotechContactsList] = useState([]);
  const [outreachMode, setOutreachMode] = useState('existing'); // 'existing' or 'manual'
  const [outreachAssets, setOutreachAssets] = useState([]);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
  const [contactSuggestIndex, setContactSuggestIndex] = useState(null);

  // Bulk email state
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailBody, setBulkEmailBody] = useState('');
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  const [bulkEmailPreview, setBulkEmailPreview] = useState(null);
  // Campaign Readiness confirmation — one final gut-check before firing
  // a large blast. Blocks direct-send: user must confirm the recipient
  // count / cost / duration estimate before the actual POST happens.
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [bulkEmailIncludePrevContacted, setBulkEmailIncludePrevContacted] = useState(false);
  const [bulkEmailProgress, setBulkEmailProgress] = useState(null); // { total, sent, failed, skipped, currentName }
  // Per-company asset map carried over from /portfolio-match. When present,
  // bulk-email backend uses these for placeholder substitution instead of the
  // oncology-locked DB pick. Cleared after the dialog closes.
  const [bulkEmailMatchedAssets, setBulkEmailMatchedAssets] = useState(null);
  const bulkEmailPollRef = useRef(null);
  const [emailMode, setEmailMode] = useState('ai'); // 'ai' or 'manual'
  // AI chat panel
  const [aiChatOpen, setAiChatOpen] = useState(false);

  const fetchCompanies = async (page = currentPage, searchQuery = search, source = sourceFilter, stageVal = stageFilter, orphanVal = orphanFilter, emailVal = emailFilter, minStageVal = minStageFilter, taVal = taFilter, modalityVal = modalityFilter, cohortIdsOverride = undefined) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 5000 });
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (source && source !== 'all') params.set('source', source);
      if (stageVal) params.set('stage', stageVal);
      if (minStageVal) params.set('min_stage', minStageVal);
      if (stagesFilter && stagesFilter.length > 0) params.set('stages', stagesFilter.join(','));
      if (orphanVal) params.set('orphan_rare', 'true');
      if (category) params.set('category', category);
      if (emailVal === 'has' || emailVal === 'none') params.set('email_filter', emailVal);
      if (taVal && taVal.trim()) params.set('ta', taVal.trim());
      if (modalityVal && modalityVal.trim()) params.set('modality', modalityVal.trim());
      // Cohort-scoped fetch — pass the cohort TAG (server resolves ids
      // internally) so we don't blow the URL length limit on 2k+ cohort
      // blasts (2,483 UUIDs = ~92KB URL, rejected by proxies). Slim=1
      // returns only display fields (id/name/email/status) shrinking
      // payload from 11MB to ~1MB and includes `all_matching_ids` so
      // "Select all N" is backend-driven, never dependent on rendered
      // rows. Fixes user-reported "Failed to load data" on Bio
      // International 2026 (2,483 companies).
      if (cohortFilter) {
        params.set('cohort', cohortFilter);
        params.set('cohort_require_email', '1');
        params.set('cohort_exclude_met', cohortExcludeMet ? '1' : '0');
        params.set('slim', '1');
      }
      const [compRes, pharmaRes, biotechContactsRes] = await Promise.all([
        axios.get(`${API}/biotech-companies?${params}`, { withCredentials: true }),
        axios.get(`${API}/pharma-contacts?limit=100000`, { withCredentials: true }),
        axios.get(`${API}/biotech-contacts`, { withCredentials: true }),
      ]);
      const investorRes = { data: [] }; // Investor module deactivated (Feb 2026)
      const compData = compRes.data;

      // Auto-fallback: user is on "My Companies" (manual) but there are 0
      // manual rows under this specialty category, yet the database has
      // some. Silently switch to "all" so the tab shows what the user
      // clearly meant to see. Avoids infinite loop by only firing when
      // source is 'manual' and total is 0 while database has entries.
      const specialtyCats = ['ndds', 'dtx', 'university', 'platform'];
      if (
        specialtyCats.includes(category) &&
        source === 'manual' &&
        (compData.total || 0) === 0 &&
        (compData.source_counts?.database || 0) > 0
      ) {
        setSourceFilter('all');
        // Re-fetch with the corrected filter — the outer setLoading(false)
        // in `finally` will handle the spinner state.
        await fetchCompanies(1, searchQuery, 'all', stageVal, orphanVal, emailVal);
        return;
      }

      setCompanies(compData.items || []);
      setTotalPages(compData.pages || 1);
      setTotalCount(compData.total || 0);
      setCurrentPage(compData.page || 1);
      // Backend-first Select-All: `all_matching_ids` is the full id
      // list matching every applied filter (cohort ∩ min_stage ∩ TA ∩
      // modality ∩ ...). Stored client-side so "Select all N" never
      // depends on how many rows have actually rendered in the table.
      setAllMatchingIds(compData.all_matching_ids || null);
      if (compData.source_counts) setSourceCounts(compData.source_counts);
      if (compData.orphan_count !== undefined) setOrphanCount(compData.orphan_count);
      setPharmaContacts(pharmaRes.data?.items || pharmaRes.data || []);
      setInvestorContacts(investorRes.data?.items || investorRes.data || []);
      setBiotechContactsList(biotechContactsRes.data?.items || biotechContactsRes.data || []);
      // Fetch assets for the current page's companies in one batch call
      const companyIds = (compData.items || []).map(c => c.id);
      if (companyIds.length > 0) {
        const assetsRes = await axios.post(`${API}/assets/batch`, { company_ids: companyIds }, { withCredentials: true });
        setAllAssets(assetsRes.data?.items || []);
      } else {
        setAllAssets([]);
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompanies(1, '', sourceFilter, stageFilter, orphanFilter); }, []);

  // Deep-link from /portfolio-match: ?ai_match_ids=id1,id2,id3 auto-selects
  // those companies and opens the bulk email dialog so the user can compose
  // an outreach to the matched portfolio.
  useEffect(() => {
    const idsParam = searchParams.get('ai_match_ids');
    if (!idsParam) return;
    const ids = idsParam.split(',').map(s => s.trim()).filter(Boolean);
    if (ids.length === 0) return;
    setSelectedCompanies(new Set(ids));
    setBulkEmailOpen(true);
    setBulkEmailPreview(null);
    // Pull the per-company asset map stashed by PortfolioMatchPage so the
    // bulk-email backend can personalise [Asset]/[Indication]/[Modality]/[Stage]
    // from the actual matched rows (not a generic oncology DB re-query).
    try {
      const stashed = sessionStorage.getItem('bulk_email_matched_assets');
      if (stashed) {
        const parsed = JSON.parse(stashed);
        if (parsed?.matched_assets && Object.keys(parsed.matched_assets).length) {
          setBulkEmailMatchedAssets(parsed.matched_assets);
        }
        sessionStorage.removeItem('bulk_email_matched_assets');
      }
    } catch { /* stale / blocked storage — non-fatal */ }
    (async () => {
      try {
        const res = await axios.post(`${API}/bulk-email/preview`, {
          company_ids: ids,
        }, { withCredentials: true });
        setBulkEmailPreview(res.data);
      } catch { toast.error('Failed to load recipients'); }
    })();
    // Strip the param so a refresh doesn't re-trigger the dialog.
    searchParams.delete('ai_match_ids');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value);
      setCurrentPage(1);
      // When searching, search across ALL sources so no company is missed
      const effectiveSource = value.trim() ? 'all' : sourceFilter;
      fetchCompanies(1, value, effectiveSource, stageFilter, orphanFilter);
    }, 400);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchCompanies(newPage, search, sourceFilter, stageFilter, orphanFilter);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSourceFilterChange = (newFilter) => {
    setSourceFilter(newFilter);
    setCurrentPage(1);
    fetchCompanies(1, search, newFilter, stageFilter, orphanFilter);
  };

  const handleStageFilterChange = (newStage) => {
    setStageFilter(newStage);
    setCurrentPage(1);
    fetchCompanies(1, search, sourceFilter, newStage, orphanFilter);
  };

  const handleOrphanFilterChange = () => {
    const newVal = !orphanFilter;
    setOrphanFilter(newVal);
    setCurrentPage(1);
    fetchCompanies(1, search, sourceFilter, stageFilter, newVal);
  };

  // 3-state cycle: all → has → none → all. Mirrors the URL so the
  // filtered view is shareable (?email=has|none). Refetches the page so
  // the count + asset_count badges reflect the filtered set.
  const handleEmailFilterChange = (newVal) => {
    setEmailFilter(newVal);
    setCurrentPage(1);
    const sp = new URLSearchParams(searchParams);
    if (newVal === 'has' || newVal === 'none') sp.set('email', newVal);
    else sp.delete('email');
    setSearchParams(sp, { replace: true });
    fetchCompanies(1, search, sourceFilter, stageFilter, orphanFilter, newVal);
  };

  const startAssetExtraction = async () => {
    try {
      const res = await axios.post(`${API}/biotech-companies/extract-assets`, {}, { withCredentials: true });
      if (res.data.status === 'already_running') toast.info('Extraction already in progress');
      else toast.success(`Extracting assets for ${res.data.total} companies`);
      pollExtractionStatus();
    } catch { toast.error('Failed to start extraction'); }
  };

  const pollExtractionStatus = () => {
    if (extractionPollRef.current) clearInterval(extractionPollRef.current);
    extractionPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/biotech-companies/extract-assets/status`, { withCredentials: true });
        setExtractionStatus(res.data);
        if (!res.data.running) {
          clearInterval(extractionPollRef.current);
          extractionPollRef.current = null;
          if (res.data.extracted > 0) {
            toast.success(`Extraction complete: ${res.data.extracted} companies processed`);
            fetchCompanies();
          }
        }
      } catch { /* ignore */ }
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      contacts: form.contacts.filter(c => c.name || c.email || c.phone),
      molecules: form.molecules.filter(m => PARAMETER_FIELDS.some(f => m[f])),
    };
    try {
      let companyId;
      if (editing) {
        await axios.put(`${API}/biotech-companies/${editing}`, payload, { withCredentials: true });
        companyId = editing;
        toast.success('Company updated');
      } else {
        const res = await axios.post(`${API}/biotech-companies`, payload, { withCredentials: true });
        companyId = res.data.id;
        toast.success('Company added');
      }
      // Upload any selected documents
      await uploadAllDocs(companyId);
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      setDocSlots([{ type: 'Non-Confidential Deck', file: null, fileName: '' }]);
      fetchCompanies();
    } catch (err) {
      toast.error('Failed to save company');
    }
  };

  const handleEdit = (company) => {
    // Migrate legacy contact fields to contacts array if needed
    const contacts = company.contacts?.length > 0
      ? company.contacts
      : company.contact_name
        ? [{ name: company.contact_name, designation: company.contact_designation || '', email: company.contact_email || '', phone: company.contact_phone || '' }]
        : [{ ...emptyContact }];
    
    // Build molecules from assets collection (source of truth) instead of stale company.molecules
    const companyAssets = allAssets.filter(a => a.company_id === company.id);
    let finalMolecules;

    if (companyAssets.length > 0) {
      finalMolecules = companyAssets.map(a => ({
        'Molecule/Product Number': a.asset_name || '',
        'Platform': a.platform || '',
        'Target': a.target || '',
        'Modality': a.modality || '',
        'Indication': a.indication || '',
        'Drug Delivery Mechanism': a.drug_delivery_mechanism || '',
        stage: a.stage || 'Pre-Clinical',
        stage_notes: a.stage_notes || '',
        molecule_description: a.molecule_description || '',
        objectives: a.objectives || '',
        deal_interests: a.deal_interests || [],
        emailing_status: a.emailing_status || 'Not Started',
        status: a.status || a.conversation_status || 'Pipeline',
      }));
    } else if (company.molecules?.length > 0) {
      // Fallback to legacy molecules if no assets yet
      finalMolecules = company.molecules.map(m => {
        if (m['Molecule/Product Number'] !== undefined) return { ...emptyMolecule, ...m };
        return {
          ...emptyMolecule,
          'Molecule/Product Number': m.name || (typeof m === 'string' ? m : ''),
          'Platform': m.platform || '',
          'Target': m.target || '',
          'Modality': m.modality || '',
          'Indication': m.indication || '',
        };
      });
    } else {
      finalMolecules = [{ ...emptyMolecule }];
    }

    setForm({
      ...company,
      contacts,
      molecules: finalMolecules,
      ncd_status: company.ncd_status || 'Not Received',
      cda_status: company.cda_status || 'Not Started',
      partnership_status: company.partnership_status || 'Not Started',
      india_licensing: !!company.india_licensing,
      company_description: company.company_description || company.description || '',
    });
    setEditing(company.id);
    setDialogOpen(true);
    // Fetch documents for this company
    fetchCompanyDocs(company.id);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/biotech-companies/${id}`, {
        withCredentials: true,
        headers: { 'x-confirm-active-delete': 'yes-delete-active-client' },
      });
      toast.success('Company deleted');
      setDeleteTarget(null);
      fetchCompanies();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to delete';
      toast.error(msg);
      setDeleteTarget(null);
    }
  };

  const fetchCompanyDocs = async (companyId) => {
    try {
      const res = await axios.get(`${API}/documents?entity_type=biotech&entity_id=${companyId}`, { withCredentials: true });
      setCompanyDocs(res.data);
    } catch { setCompanyDocs([]); }
  };

  // Contact management (up to 3)
  const updateContact = (index, field, value) => {
    setForm(prev => {
      const contacts = [...prev.contacts];
      contacts[index] = { ...contacts[index], [field]: value };
      return { ...prev, contacts };
    });
  };

  const addContact = () => {
    setForm(prev => ({ ...prev, contacts: [...prev.contacts, { ...emptyContact }] }));
  };

  const removeContact = (index) => {
    if (form.contacts.length <= 1) return;
    setForm(prev => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== index) }));
  };

  const getContactSuggestions = (nameValue) => {
    if (!nameValue || nameValue.length < 2) return [];
    const term = nameValue.toLowerCase();
    return biotechContactsList.filter(bc =>
      bc.name?.toLowerCase().includes(term) ||
      bc.company_name?.toLowerCase().includes(term)
    ).slice(0, 5);
  };

  const selectContactSuggestion = (index, suggestion) => {
    setForm(prev => {
      const contacts = [...prev.contacts];
      contacts[index] = {
        name: suggestion.name || '',
        designation: suggestion.designation || '',
        email: suggestion.email || '',
        phone: suggestion.phone || '',
      };
      // Auto-fill company-level fields from the contact's data
      const updates = { contacts };
      if (suggestion.company_name && !prev.company_name) {
        updates.company_name = suggestion.company_name;
      }
      if (suggestion.location && !prev.location) {
        updates.location = suggestion.location;
      }
      if (suggestion.website && !prev.website) {
        updates.website = suggestion.website;
      }
      return { ...prev, ...updates };
    });
    setContactSuggestIndex(null);
  };

  // Parameter management
  const updateMolecule = (index, field, value) => {
    setForm(prev => {
      const molecules = [...prev.molecules];
      molecules[index] = { ...molecules[index], [field]: value };
      return { ...prev, molecules };
    });
  };

  const addMolecule = () => {
    setForm(prev => ({ ...prev, molecules: [...prev.molecules, { ...emptyMolecule }] }));
  };

  const removeMolecule = (index) => {
    if (form.molecules.length <= 1) return;
    setForm(prev => ({ ...prev, molecules: prev.molecules.filter((_, i) => i !== index) }));
  };

  // Document Upload
  const DOC_TYPE_OPTIONS = ['Non-Confidential Deck', 'Corporate Overview', 'CDA', 'Partnership Agreement', 'Other'];

  const addDocSlot = () => {
    if (docSlots.length >= 5) { toast.info('Maximum 5 documents per upload'); return; }
    setDocSlots(prev => [...prev, { type: 'Corporate Overview', file: null, fileName: '' }]);
  };

  const removeDocSlot = (index) => {
    if (docSlots.length <= 1) return;
    setDocSlots(prev => prev.filter((_, i) => i !== index));
  };

  const updateDocSlotType = (index, type) => {
    setDocSlots(prev => prev.map((s, i) => i === index ? { ...s, type } : s));
  };

  const handleFileSelect = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocSlots(prev => prev.map((s, i) => i === index ? { ...s, file, fileName: file.name } : s));
  };

  const uploadAllDocs = async (entityId) => {
    const slotsWithFiles = docSlots.filter(s => s.file);
    if (slotsWithFiles.length === 0) return;
    setUploading(true);
    try {
      for (const slot of slotsWithFiles) {
        const formData = new FormData();
        formData.append('file', slot.file);
        // CRITICAL: backend expects these as Form fields, NOT query params.
        // Sending them as ?query= silently fails (defaults to entity_type='general',
        // entity_id='', doc_type='general') → orphan docs detached from the biotech.
        formData.append('doc_type', slot.type);
        formData.append('entity_type', 'biotech');
        formData.append('entity_id', entityId);
        const { data: uploadedDoc, skipped } = await uploadDocumentWithDedupe(formData);
        if (skipped) continue;
        const docId = uploadedDoc?.id || '';
        // Auto-link CDA or Partnership Agreement to the company
        if (slot.type === 'CDA' && docId) {
          await axios.patch(`${API}/biotech-companies/${entityId}`, { cda_status: 'Signed', cda_document_id: docId }, { withCredentials: true });
          setForm(prev => ({ ...prev, cda_status: 'Signed' }));
        }
        if (slot.type === 'Partnership Agreement' && docId) {
          await axios.patch(`${API}/biotech-companies/${entityId}`, { partnership_status: 'Signed', partnership_document_id: docId }, { withCredentials: true });
          setForm(prev => ({ ...prev, partnership_status: 'Signed' }));
        }
      }
      const hasNcd = slotsWithFiles.some(s => s.type === 'Non-Confidential Deck' || s.type === 'Corporate Overview');
      if (hasNcd) {
        setForm(prev => ({ ...prev, ncd_status: 'Received' }));
        toast.success(`${slotsWithFiles.length} document(s) uploaded — AI is auto-populating the CRM in the background`);
      } else {
        toast.success(`${slotsWithFiles.length} document(s) uploaded`);
      }
      if (entityId && entityId !== 'pending') fetchCompanyDocs(entityId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 413 ? 'File too large — try a smaller PDF (limit ~10MB)' : '')
        || (status === 422 ? 'Invalid file type' : '')
        || (status === 409 && detail?.code === 'duplicate_document' ? 'This file is already uploaded for this biotech — open the company detail page to manage it' : '')
        || (status ? `Upload failed (HTTP ${status})` : err?.message)
        || 'Upload failed';
      toast.error(msg, { duration: 8000 });
      console.error('[biotech doc upload error]', { status, detail, err });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, { withCredentials: true });
      toast.success('Document removed');
      setDeleteDocTarget(null);
      if (editing) fetchCompanyDocs(editing);
    } catch { toast.error('Failed to delete'); setDeleteDocTarget(null); }
  };

  // Manual emailing_status override — invoked from the clickable per-asset
  // status badge on the company row. Optimistically flips local state, then
  // PATCHes the backend. Reverts on failure.
  const handleEmailingStatusChange = async (assetId, newStatus) => {
    setAllAssets(prev => prev.map(a => a.id === assetId ? { ...a, emailing_status: newStatus } : a));
    try {
      await axios.patch(
        `${API}/assets/${assetId}/emailing-status`,
        { status: newStatus },
        { withCredentials: true },
      );
      toast.success(`Marked ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update status');
      fetchCompanies();
    }
  };

  // Outreach functions
  const fetchOutreach = async (companyId) => {
    try {
      const res = await axios.get(`${API}/outreach?biotech_company_id=${companyId}`, { withCredentials: true });
      setOutreachMap(prev => ({ ...prev, [companyId]: res.data }));
    } catch { /* ignore */ }
  };


  const openOutreachDialog = async (companyId, outreach = null) => {
    setOutreachCompanyId(companyId);
    if (outreach) {
      setOutreachForm(outreach);
      setEditingOutreach(outreach.id);
      setOutreachMode('manual');
    } else {
      setOutreachForm({ ...emptyOutreachForm, biotech_company_id: companyId });
      setEditingOutreach(null);
      setOutreachMode('existing');
    }
    setOutreachDialogOpen(true);
    try {
      const res = await axios.get(`${API}/assets?company_id=${companyId}`, { withCredentials: true });
      const fetchedAssets = res.data?.items || res.data || [];
      setOutreachAssets(fetchedAssets);
      // Auto-select if single asset
      if (fetchedAssets.length === 1 && !outreach) {
        setOutreachForm(p => ({ ...p, asset_id: fetchedAssets[0].id, asset_name: fetchedAssets[0].asset_name }));
      }
    } catch { setOutreachAssets([]); }
  };

  const toggleExistingContact = (contactId, type) => {
    setOutreachSelectedIds(prev => {
      const exists = prev.includes(contactId);
      const next = exists ? prev.filter(x => x !== contactId) : [...prev, contactId];
      // Mirror the first selected contact into the single-record form fields
      // so the existing submit path (and edit mode) still works for size 1.
      if (next.length === 0) {
        setOutreachForm(p => ({ ...emptyOutreachForm, biotech_company_id: p.biotech_company_id, contact_type: type, asset_id: p.asset_id, asset_name: p.asset_name, status: p.status, status_notes: p.status_notes }));
      } else {
        const firstId = next[0];
        if (type === 'Pharma') {
          const c = pharmaContacts.find(x => x.id === firstId);
          if (c) setOutreachForm(prev2 => ({
            ...prev2,
            contact_name: c.name,
            contact_email: c.email || '',
            company_name: c.company_name || '',
            country: c.location || '',
            designation: c.designation || '',
            contact_type: 'Pharma',
            source_id: c.id,
          }));
        } else {
          const c = investorContacts.find(x => x.id === firstId);
          if (c) setOutreachForm(prev2 => ({
            ...prev2,
            contact_name: c.contact_person || c.name,
            contact_email: c.email || '',
            company_name: c.firm_name || c.name || '',
            country: c.location || '',
            designation: c.designation || '',
            contact_type: 'Investor',
            source_id: c.id,
          }));
        }
      }
      return next;
    });
  };

  // Legacy single-select shim — used elsewhere (e.g. edit flow). Keeps
  // existing call sites working unchanged.
  const selectExistingContact = (contactId, type) => {
    setOutreachSelectedIds([contactId]);
    if (type === 'Pharma') {
      const contact = pharmaContacts.find(c => c.id === contactId);
      if (contact) {
        setOutreachForm(prev => ({
          ...prev,
          contact_name: contact.name,
          contact_email: contact.email || '',
          company_name: contact.company_name || '',
          country: contact.location || '',
          designation: contact.designation || '',
          contact_type: 'Pharma',
          source_id: contact.id,
        }));
      }
    } else {
      const contact = investorContacts.find(c => c.id === contactId);
      if (contact) {
        setOutreachForm(prev => ({
          ...prev,
          contact_name: contact.contact_person || contact.name,
          contact_email: contact.email || '',
          company_name: contact.firm_name || contact.name || '',
          country: contact.location || '',
          designation: contact.designation || '',
          contact_type: 'Investor',
          source_id: contact.id,
        }));
      }
    }
  };


  const handleOutreachSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOutreach) {
        await axios.put(`${API}/outreach/${editingOutreach}`, outreachForm, { withCredentials: true });
        toast.success('Outreach updated');
      } else if (outreachMode === 'existing' && outreachSelectedIds.length > 1) {
        // Multi-select: create one outreach row per chosen contact.
        const type = outreachForm.contact_type;
        const list = type === 'Pharma' ? pharmaContacts : type === 'Biotech' ? biotechContactsList : investorContacts;
        let created = 0, dupes = 0, failed = 0;
        for (const cid of outreachSelectedIds) {
          const c = list.find(x => x.id === cid);
          if (!c) { failed++; continue; }
          const payload = {
            ...outreachForm,
            contact_type: type,
            source_id: c.id,
            contact_name: type === 'Investor' ? (c.contact_person || c.name) : c.name,
            contact_email: c.email || '',
            company_name: type === 'Investor' ? (c.firm_name || c.name || '') : (c.company_name || ''),
            country: c.location || '',
            designation: c.designation || '',
          };
          try {
            await axios.post(`${API}/outreach`, payload, { withCredentials: true });
            created++;
          } catch (err) {
            if (err.response?.status === 409) dupes++;
            else failed++;
          }
        }
        if (created) toast.success(`Added ${created} outreach contact${created > 1 ? 's' : ''}`);
        if (dupes) toast(`${dupes} contact${dupes > 1 ? 's were' : ' was'} already tagged — skipped`, { icon: '⚠️' });
        if (failed) toast.error(`${failed} contact${failed > 1 ? 's' : ''} failed to save`);
      } else {
        await axios.post(`${API}/outreach`, outreachForm, { withCredentials: true });
        toast.success('Outreach added');
        // ── Manual contact auto-upsert into Pharma Contacts database ──
        // When the user adds an outreach manually for a Pharma contact
        // that isn't in our system yet, seed `pharma_contacts` so the
        // same person shows up next time under "From Existing Contacts".
        // Idempotent on duplicate email (backend returns 409, treat as
        // success — record already exists, do not surface as error).
        // 2026-06-06 user request: "once the manual entry happens, the
        // same contact should start getting reflected in the pharma
        // contact database as well. So it should go seamless that way."
        if (outreachMode === 'manual'
            && outreachForm.contact_type === 'Pharma'
            && (outreachForm.contact_name || '').trim()
            && (outreachForm.contact_email || '').trim()) {
          try {
            await axios.post(`${API}/pharma-contacts`, {
              name: outreachForm.contact_name.trim(),
              email: outreachForm.contact_email.trim().toLowerCase(),
              company_name: outreachForm.company_name || '',
              designation: outreachForm.designation || '',
              location: outreachForm.country || '',
            }, { withCredentials: true });
            toast.success(`✓ ${outreachForm.contact_name} added to Pharma Contacts`, { duration: 5000 });
          } catch (err) {
            // 409 = already exists — that's a success state for an
            // idempotent upsert, not an error to surface.
            if (err?.response?.status !== 409) {
              toast(`Outreach saved, but couldn't add to Pharma Contacts: ${err?.response?.data?.detail || 'unknown error'}`, { icon: '⚠️', duration: 5000 });
            }
          }
        }
      }
      setOutreachDialogOpen(false);
      setEditingOutreach(null);
      setOutreachSelectedIds([]);
      if (outreachCompanyId) fetchOutreach(outreachCompanyId);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data?.detail || 'Outreach for this contact already exists');
      } else {
        toast.error('Failed to save outreach');
      }
    }
  };

  const handleOutreachStatusChange = async (outreachId, companyId, newStatus) => {
    try {
      await axios.patch(`${API}/outreach/${outreachId}/status`, { status: newStatus }, { withCredentials: true });
      fetchOutreach(companyId);
    } catch { toast.error('Failed to update status'); }
  };

  const handleDeleteOutreach = async (outreachId, companyId) => {
    try {
      await axios.delete(`${API}/outreach/${outreachId}`, { withCredentials: true });
      toast.success('Outreach removed');
      setDeleteOutreachTarget(null);
      fetchOutreach(companyId);
    } catch { toast.error('Failed to delete'); setDeleteOutreachTarget(null); }
  };


  // Pending Outreach predicate. A biotech qualifies when:
  //   - has ≥1 visible asset stuck at "Not Started" (or no emailing_status)
  //   - has ≥1 contact email on file (otherwise we can't email them)
  //   - is NOT flagged Do-Not-Contact
  // This is the BD "next action" queue: assets where we have freehand to
  // run a campaign but haven't found time to map+dispatch yet.
  // A biotech is "Pending Outreach" only when:
  //   1. It's in My Companies (`is_my_company`) — pending outreach is meaningful
  //      only for biotechs the user is actively driving deals on, not for
  //      every prospect in the DB.
  //   2. It is not on the Do Not Contact list.
  //   3. At least one contact has an email.
  //   4. At least one of its (non-hidden) assets has not started outreach.
  const isPendingOutreach = (c) => {
    if (!c.is_my_company) return false;
    if (c.do_not_contact?.flagged) return false;
    const hasEmail = (c.contacts || []).some(ct => (ct.email || '').trim());
    if (!hasEmail) return false;
    return allAssets.some(a =>
      a.company_id === c.id
      && !a.is_hidden
      && (!a.emailing_status || a.emailing_status === 'Not Started')
    );
  };

  const filtered = companies.filter(c => {
    const matchesActive = activeFilter ? !INACTIVE_STATUSES.includes(c.company_status) : true;
    const matchesAgreement = agreementFilter ? (c.partnership_status || 'Not Started') === agreementFilter : true;
    const matchesEmailing = emailingFilter ? allAssets.some(a => a.company_id === c.id && a.emailing_status === emailingFilter) : true;
    const matchesDnc = dncFilter ? !!c.do_not_contact?.flagged : true;
    const matchesPending = pendingOutreachFilter ? isPendingOutreach(c) : true;
    const matchesCohort = (cohortFilter && cohortIds) ? cohortIds.has(c.id) : true;
    return matchesActive && matchesAgreement && matchesEmailing && matchesDnc && matchesPending && matchesCohort;
  });
  const dncCount = companies.filter(c => !!c.do_not_contact?.flagged).length;
  const pendingOutreachCount = companies.filter(isPendingOutreach).length;

  // Cohort filter loader: when user picks a conference cohort, fetch the
  // canonical id list once and keep it client-side. Re-fetch when the
  // exclude-Met-In-Person toggle flips. Also fetch a broader cohort
  // membership count (with-email + without-email) so the UI can show a
  // clear breakdown: "N total · M with email · K matching your filters".
  useEffect(() => {
    if (!cohortFilter) {
      // Clearing the cohort: reset ids + meta, then refetch the full
      // (tab-scoped) list so the table doesn't stay narrowed to zero.
      setCohortIds(null);
      setCohortMeta(null);
      fetchCompanies(1, search, sourceFilter, stageFilter, orphanFilter, emailFilter, minStageFilter, taFilter, modalityFilter, null);
      return;
    }
    let cancelled = false;
    (async () => {
      setCohortLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('exclude_met_in_person', cohortExcludeMet ? '1' : '0');
        params.set('require_email', '1');
        // Fetch both variants in parallel so the UI can show "2,483 total
        // · 2,145 with email" (the 338 difference explains the drop from
        // dropdown label to actionable set).
        const [emailedRes, allRes] = await Promise.all([
          axios.get(
            `${API}/cohorts/${encodeURIComponent(cohortFilter)}/biotech-ids?${params.toString()}`,
            { withCredentials: true },
          ),
          axios.get(
            `${API}/cohorts/${encodeURIComponent(cohortFilter)}/biotech-ids?require_email=0&exclude_met_in_person=${cohortExcludeMet ? '1' : '0'}`,
            { withCredentials: true },
          ),
        ]);
        if (cancelled) return;
        const emailedIds = new Set(emailedRes.data.ids || []);
        setCohortIds(emailedIds);
        setCohortMeta({
          total: allRes.data.total || 0,
          withEmail: emailedRes.data.total || 0,
          excludedMet: emailedRes.data.excluded_met_in_person || 0,
          excludedNoEmail: emailedRes.data.excluded_no_email || 0,
        });
        // Refetch companies scoped to the cohort so the table shows the
        // right rows regardless of source tab (fixes "All Companies fails
        // to load" — the fetch is now capped to ~2,500 rows not 4k+).
        fetchCompanies(1, search, sourceFilter, stageFilter, orphanFilter, emailFilter, minStageFilter, taFilter, modalityFilter, emailedIds);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Could not load cohort');
      } finally {
        if (!cancelled) setCohortLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortFilter, cohortExcludeMet]);

  const getContactDisplay = (company) => {
    if (company.contacts?.length > 0) {
      return company.contacts.filter(c => c.name);
    }
    if (company.contact_name) {
      return [{ name: company.contact_name, email: company.contact_email, designation: company.contact_designation }];
    }
    return [];
  };

  const getParameterDisplay = (company) => {
    if (!company.molecules?.length) return [];
    return company.molecules.map(m => {
      // New fixed-field format
      if (m['Molecule/Product Number'] !== undefined) {
        return PARAMETER_FIELDS.filter(f => m[f]).map(f => ({ parameter: f, details: m[f] }));
      }
      // Legacy parameter/details format
      if (m.parameter && m.details) return [{ parameter: m.parameter, details: m.details }];
      // Legacy name format
      if (typeof m === 'string') return [{ parameter: 'Molecule/Product Number', details: m }];
      if (m.name) {
        const items = [{ parameter: 'Molecule/Product Number', details: m.name }];
        if (m.target) items.push({ parameter: 'Target', details: m.target });
        if (m.modality) items.push({ parameter: 'Modality', details: m.modality });
        if (m.indication) items.push({ parameter: 'Indication', details: m.indication });
        return items;
      }
      return [];
    }).flat();
  };

  const handleAiMatch = async () => {
    if (!aiMatchQuery.trim()) { toast.error('Please enter the contact\'s interests'); return; }
    setAiMatchLoading(true);
    setAiMatchResult(null);
    setAiMatchOpen(true);
    try {
      const filterMap = { manual: 'active', bio_international: 'database', all: 'all' };
      const res = await axios.post(`${API}/ai/match-portfolio`, {
        interests: aiMatchQuery,
        match_type: aiMatchType,
        source_filter: filterMap[sourceFilter] || 'active',
        stage_filter: aiStageFilter,
      }, { withCredentials: true });
      setAiMatchResult(res.data);
      // Auto-select all matches with company_id
      const ids = new Set((res.data?.matches || []).filter(m => m.company_id).map(m => m.company_id));
      setAiSelectedMatches(ids);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to match portfolio');
      setAiMatchResult(null);
    } finally {
      setAiMatchLoading(false);
    }
  };

  const PHASE_ORDER = { 'Commercial': 0, 'Phase 3': 1, 'Phase 2': 2, 'Phase 1': 3, 'Clinical Stage': 4, 'IND': 5, 'Pre-Clinical': 6 };
  const sortByPhase = (rows) => [...rows].sort((a, b) => (PHASE_ORDER[a.phase] ?? 99) - (PHASE_ORDER[b.phase] ?? 99));

  const copyMatchTable = () => {
    if (!aiMatchResult?.matches?.length) return;
    const rows = sortByPhase(aiMatchResult.matches);
    const isInvestor = aiMatchType === 'investor';
    const headers = isInvestor
      ? '<th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Company</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Molecule</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Indication</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Phase</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Fundraising</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Fit</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Rationale</th>'
      : '<th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Company</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Molecule</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Indication</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Phase</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Fit</th><th style="border:1px solid #94a3b8;padding:8px;text-align:left;">Rationale</th>';
    const html = `<table border="1" cellpadding="8" cellspacing="0" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:13px;border:1px solid #94a3b8;">
<tr style="background:#f1f5f9;">${headers}</tr>
${rows.map(r => {
  const s = 'border:1px solid #cbd5e1;padding:8px;';
  return isInvestor
    ? `<tr><td style="${s}">${r.company || ''}</td><td style="${s}">${r.molecule || ''}</td><td style="${s}">${r.indication || ''}</td><td style="${s}">${r.phase || ''}</td><td style="${s}">${r.fundraising || ''}</td><td style="${s}">${r.fit_strength || 'Moderate'}</td><td style="${s}">${r.match_reason || ''}</td></tr>`
    : `<tr><td style="${s}">${r.company || ''}</td><td style="${s}">${r.molecule || ''}</td><td style="${s}">${r.indication || ''}</td><td style="${s}">${r.phase || ''}</td><td style="${s}">${r.fit_strength || 'Moderate'}</td><td style="${s}">${r.match_reason || ''}</td></tr>`;
}).join('\n')}
</table>`;
    const plainHeaders = isInvestor ? 'Company\tMolecule\tIndication\tPhase\tFundraising\tFit\tRationale' : 'Company\tMolecule\tIndication\tPhase\tFit\tRationale';
    const plain = plainHeaders + '\n' +
      rows.map(r => isInvestor
        ? `${r.company || ''}\t${r.molecule || ''}\t${r.indication || ''}\t${r.phase || ''}\t${r.fundraising || ''}\t${r.fit_strength || 'Moderate'}\t${r.match_reason || ''}`
        : `${r.company || ''}\t${r.molecule || ''}\t${r.indication || ''}\t${r.phase || ''}\t${r.fit_strength || 'Moderate'}\t${r.match_reason || ''}`
      ).join('\n');
    const blob = new Blob([html], { type: 'text/html' });
    const plainBlob = new Blob([plain], { type: 'text/plain' });
    navigator.clipboard.write([new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob })]).then(() => {
      toast.success('Table copied — paste into email for formatted table');
    }).catch(() => {
      navigator.clipboard.writeText(plain).then(() => toast.success('Table copied as text'));
    });
  };

  const openBulkEmailFromMatches = async () => {
    if (aiSelectedMatches.size === 0) { toast.error('Select at least one company'); return; }
    // Use the existing bulk email flow with the selected company_ids
    setSelectedCompanies(aiSelectedMatches);
    // Pull the matched-asset detail per company from the in-page match results
    // so [Asset]/[Indication]/[Modality]/[Stage] substitute from the actual
    // matched row, not the oncology-locked DB pick. First selected row wins
    // when a company has multiple matching assets.
    const matchedAssets = {};
    for (const row of (aiMatchResult?.matches || [])) {
      if (!row.company_id || !aiSelectedMatches.has(row.company_id)) continue;
      if (matchedAssets[row.company_id]) continue;
      matchedAssets[row.company_id] = {
        asset_name: row.molecule || '',
        indication: row.indication || '',
        modality: row.modality || '',
        stage: row.phase || '',
        therapeutic_area: row.indication || '',
      };
    }
    setBulkEmailMatchedAssets(Object.keys(matchedAssets).length ? matchedAssets : null);
    setAiMatchOpen(false);
    setBulkEmailOpen(true);
    setBulkEmailPreview(null);
    try {
      const res = await axios.post(`${API}/bulk-email/preview`, { company_ids: Array.from(aiSelectedMatches) }, { withCredentials: true });
      setBulkEmailPreview(res.data);
    } catch { toast.error('Failed to load recipients'); }
  };

  const toggleMatchSelect = (companyId) => {
    setAiSelectedMatches(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const toggleSelectAllMatches = () => {
    if (!aiMatchResult?.matches) return;
    const allIds = aiMatchResult.matches.filter(m => m.company_id).map(m => m.company_id);
    const uniqueIds = [...new Set(allIds)];
    if (aiSelectedMatches.size >= uniqueIds.length) {
      setAiSelectedMatches(new Set());
    } else {
      setAiSelectedMatches(new Set(uniqueIds));
    }
  };


  const toggleMyCompany = async (companyId) => {
    try {
      const res = await axios.patch(`${API}/biotech-companies/${companyId}/toggle-my-company`, {}, { withCredentials: true });
      setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, is_my_company: res.data.is_my_company } : c));
      toast.success(res.data.is_my_company ? 'Added to My Companies' : 'Removed from My Companies');
      // Refresh source counts
      const params = new URLSearchParams({ page: 1, limit: 5000 });
      if (sourceFilter && sourceFilter !== 'all') params.set('source', sourceFilter);
      const compRes = await axios.get(`${API}/biotech-companies?${params}`, { withCredentials: true });
      if (compRes.data.source_counts) setSourceCounts(compRes.data.source_counts);
    } catch { toast.error('Failed to update'); }
  };


  const handleShareStatus = async (companyId) => {
    try {
      const res = await axios.post(`${API}/share/${companyId}`, {}, { withCredentials: true });
      const url = `${window.location.origin}/status/${res.data.share_token}`;
      await navigator.clipboard.writeText(url);
      window.open(url, '_blank');
      toast.success('Share link copied & opened in new tab');
    } catch {
      toast.error('Failed to generate share link');
    }
  };


  const copyCrmTemplate = (company) => {
    const companyAssets = allAssets.filter(a => a.company_id === company.id);
    const assetsList = companyAssets.map((a, i) => {
      return `--- Asset / Molecule ${i + 1} ---
Molecule/Product Number: ${a.asset_name || ''}
Development Stage: ${a.stage || ''}
Stage Notes: ${a.stage_notes || ''}
Platform: ${a.platform || ''}
Target: ${a.target || ''}
Modality: ${a.modality || ''}
Indication: ${a.indication || ''}
Drug Delivery Mechanism: ${a.drug_delivery_mechanism || ''}
Asset Description: ${a.molecule_description || a.description || ''}
Objectives: ${a.objectives || ''}
Looking For: ${(a.deal_interests || []).join(', ') || ''}
Emailing Status: ${a.emailing_status || 'Not Started'}
Status: ${a.status || 'Pipeline'}`;
    }).join('\n\n');

    const contactsList = (company.contacts || []).map((c, i) => {
      return `--- Contact ${i + 1} ---
Full Name: ${c.name || ''}
Designation: ${c.designation || ''}
Email: ${c.email || ''}
Phone: ${c.phone || ''}`;
    }).join('\n\n');

    const template = `--- Company Information ---
Company Name: ${company.company_name || ''}
Location: ${company.location || ''}
Website: ${company.website || ''}
Therapeutic Area: ${company.therapeutic_area || ''}
Company Description: ${company.company_description || company.description || ''}

${contactsList || '--- Contact ---\nFull Name: \nDesignation: \nEmail: \nPhone: '}

${assetsList || '--- Asset / Molecule 1 ---\nMolecule/Product Number: \nDevelopment Stage: \nStage Notes: \nPlatform: \nTarget: \nModality: \nIndication: \nDrug Delivery Mechanism: \nAsset Description: \nObjectives: \nLooking For: \nEmailing Status: \nStatus: '}`;

    navigator.clipboard.writeText(template).then(() => {
      toast.success(`CRM data for ${company.company_name} copied`);
    }).catch(() => toast.error('Failed to copy'));
  };

  // Bulk email handlers
  const scanForCdmos = async () => {
    setCdmoScanning(true);
    setCdmoFlagged([]);
    setCdmoSelected(new Set());
    try {
      const res = await axios.get(`${API}/biotech-companies/scan/non-target`, { withCredentials: true });
      setCdmoFlagged(res.data.flagged || []);
      if ((res.data.flagged || []).length === 0) {
        toast.success('No non-target entities found — database is clean');
      }
    } catch {
      toast.error('Scan failed');
    } finally {
      setCdmoScanning(false);
    }
  };

  const removeFlaggedCdmos = async () => {
    if (cdmoSelected.size === 0) return;
    setCdmoRemoving(true);
    try {
      const items = cdmoFlagged.filter(f => cdmoSelected.has(f.id)).map(f => ({ id: f.id, collection: f.collection }));
      await axios.delete(`${API}/biotech-companies/bulk-remove`, { data: { items }, withCredentials: true });
      toast.success(`Removed ${items.length} non-target entit${items.length === 1 ? 'y' : 'ies'}`);
      setCdmoFlagged(prev => prev.filter(f => !cdmoSelected.has(f.id)));
      setCdmoSelected(new Set());
      fetchCompanies();
    } catch {
      toast.error('Failed to remove');
    } finally {
      setCdmoRemoving(false);
    }
  };

  const toggleSelectCompany = (companyId) => {
    setSelectedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCompanies.size === filtered.length) {
      setSelectedCompanies(new Set());
    } else {
      setSelectedCompanies(new Set(filtered.map(c => c.id)));
    }
  };

  const openBulkEmail = async () => {
    if (selectedCompanies.size === 0) return;
    setBulkEmailOpen(true);
    setBulkEmailPreview(null);
    try {
      const res = await axios.post(`${API}/bulk-email/preview`, {
        company_ids: Array.from(selectedCompanies),
        include_previously_contacted: bulkEmailIncludePrevContacted,
      }, { withCredentials: true });
      setBulkEmailPreview(res.data);
    } catch { toast.error('Failed to load recipients'); }
  };

  // Re-preview whenever the user toggles the "include previously contacted" checkbox
  // so the button reflects the true send count.
  useEffect(() => {
    if (!bulkEmailOpen || selectedCompanies.size === 0 || bulkEmailSending) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/bulk-email/preview`, {
          company_ids: Array.from(selectedCompanies),
          include_previously_contacted: bulkEmailIncludePrevContacted,
        }, { withCredentials: true });
        if (!cancelled) setBulkEmailPreview(res.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkEmailIncludePrevContacted]);

  const sendTestEmail = async () => {
    if (!bulkEmailSubject.trim() || !bulkEmailBody.trim()) {
      toast.error('Subject and email body are required');
      return;
    }
    setBulkEmailSending(true);
    try {
      const res = await axios.post(`${API}/bulk-email/biotech-companies/test`, {
        company_ids: Array.from(selectedCompanies),
        subject: bulkEmailSubject,
        email_body: bulkEmailBody,
        test_count: 3,
        matched_assets: bulkEmailMatchedAssets || undefined,
      }, { withCredentials: true });
      toast.success(`Sent ${res.data.test_count} test email(s) to ${res.data.sent_to_inbox}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send tests');
    }
    setBulkEmailSending(false);
  };

  const resetBulkEmailState = () => {
    setBulkEmailSubject('');
    setBulkEmailBody('');
    setSelectedCompanies(new Set());
    setBulkEmailPreview(null);
    setBulkEmailMatchedAssets(null);
    setEmailMode('ai');
  };

  const sendBulkEmail = async () => {
    if (!bulkEmailSubject.trim() || !bulkEmailBody.trim()) {
      toast.error('Subject and email body are required');
      return;
    }
    setBulkEmailSending(true);
    try {
      const res = await axios.post(`${API}/bulk-email/biotech-companies`, {
        company_ids: Array.from(selectedCompanies),
        subject: bulkEmailSubject,
        email_body: bulkEmailBody,
        include_previously_contacted: bulkEmailIncludePrevContacted,
        matched_assets: bulkEmailMatchedAssets || undefined,
      }, { withCredentials: true });
      const { total_recipients, skipped, total_companies } = res.data;
      const skipNote = skipped > 0 ? ` (${skipped} skipped — already contacted / bounced / duplicate)` : '';
      toast.success(`Sending ${total_recipients} emails from ${total_companies} selected companies${skipNote}`);
      setBulkEmailProgress({ status: 'running', total: total_recipients, sent: 0, failed: 0, skipped, currentName: '' });
      const taskId = res.data.task_id;
      bulkEmailPollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/bulk-email/status/${taskId}`, { withCredentials: true });
          setBulkEmailProgress({
            status: statusRes.data.status || 'running',
            total: statusRes.data.total || total_recipients,
            sent: statusRes.data.sent || 0,
            failed: statusRes.data.failed || 0,
            skipped: statusRes.data.skipped || skipped,
            currentName: statusRes.data.current_name || '',
          });
          if (statusRes.data.status === 'completed') {
            clearInterval(bulkEmailPollRef.current);
            toast.success(`Sent ${statusRes.data.sent} emails successfully${statusRes.data.failed > 0 ? `, ${statusRes.data.failed} failed` : ''}`);
            // Keep the dialog open briefly so the user sees the green "All N sent!" banner
            setTimeout(() => {
              setBulkEmailSending(false);
              setBulkEmailOpen(false);
              setBulkEmailProgress(null);
              resetBulkEmailState();
            }, 1800);
          } else if (statusRes.data.status === 'failed') {
            clearInterval(bulkEmailPollRef.current);
            toast.error('Bulk email task failed');
            setBulkEmailSending(false);
          }
        } catch { /* keep polling */ }
      }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send emails');
      setBulkEmailSending(false);
    }
  };



  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="biotech-companies-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {pageTitle || (
              category === 'ndds' ? 'Formulation · Delivery' :
              category === 'university' ? 'Academic · Translational' :
              category === 'dtx' ? 'DTx · Companion Digital' :
              category === 'platform' ? 'Platform · Discovery' :
              category === 'general_biotech' ? 'Biotech Companies' :
              (propCategory ? 'Biotech Companies' : 'Out-Licensers')
            )}
            {!propCategory && (
              <span className="ml-3 text-sm font-normal text-slate-500 tabular-nums" data-testid="out-licenser-total-count">
                · {(sourceCounts?.all || totalCount || 0).toLocaleString()} total
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{
            category === 'university' ? 'Academic institutions with licensable assets and research' :
            category === 'platform' ? 'Drug delivery, gene therapy, and technology platform companies' :
            category === 'dtx' ? 'Digital therapeutics and complementary digital health platforms' :
            'Manage small biotech companies seeking out-licensing or funding'
          }</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Import Conference File + History buttons moved to Data Ops →
              Conference Imports (2026-02). This page is now read-only for
              import operations — discover, enrich, and outreach happen here;
              uploading happens in Data Ops. */}
          {/* ── Bulk Perplexity Enrich All Biotechs ─────────────────────── */}
          {/* "All enriched" congrats pill — shown when qualifying=0 and no
              active job. Replaces the invisible/hidden state so the user
              knows there's nothing left to do. Auto-dismisses when they
              add more thin biotechs (qualifying goes back > 0). */}
          {pplxEnrich && pplxEnrich.qualifying === 0 && !['running', 'queued'].includes(pplxEnrich.job?.status) && (
            <span
              className="text-[11px] font-medium text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-md px-2.5 py-1.5 flex items-center gap-1.5"
              title="Every biotech has at least 3 of the tracked fields populated — nothing to enrich."
              data-testid="all-enriched-pill"
            >
              <Check className="w-3.5 h-3.5 text-emerald-600" />
              All biotechs enriched
            </span>
          )}
          {/* Last-result banner — shows the summary of the just-finished
              run (enriched / failed / cancelled) with a dismiss button.
              Sticks until the user starts a new run or clicks × so they
              don't lose the outcome by scrolling or switching tabs. */}
          {pplxLastResult && !['running', 'queued'].includes(pplxEnrich?.job?.status) && (
            <span
              className={`text-[11px] font-medium rounded-md px-2 py-1.5 flex items-center gap-1.5 ${
                pplxLastResult.error
                  ? 'text-rose-800 bg-rose-50 border border-rose-300'
                  : pplxLastResult.cancelled
                    ? 'text-slate-700 bg-slate-50 border border-slate-300'
                    : pplxLastResult.failed > 0
                      ? 'text-amber-800 bg-amber-50 border border-amber-300'
                      : 'text-emerald-800 bg-emerald-50 border border-emerald-300'
              }`}
              data-testid="pplx-last-result"
            >
              {pplxLastResult.error
                ? <>❌ Failed: {pplxLastResult.error}</>
                : pplxLastResult.cancelled
                  ? <>⏸ {pplxLastResult.enriched} enriched before cancel</>
                  : pplxLastResult.failed > 0
                    ? <>⚠️ {pplxLastResult.enriched} enriched · {pplxLastResult.failed} failed</>
                    : <>✅ {pplxLastResult.enriched} newly enriched</>}
              <button
                type="button"
                onClick={() => setPplxLastResult(null)}
                className="text-current opacity-50 hover:opacity-100 ml-1"
                title="Dismiss"
                data-testid="pplx-last-result-dismiss"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {pplxEnrich && (pplxEnrich.qualifying > 0 || ['running', 'queued', 'error'].includes(pplxEnrich.job?.status)) && (
            <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              onClick={
                ['running', 'queued'].includes(pplxEnrich.job?.status)
                  ? cancelPplxEnrich
                  : kickPplxEnrich
              }
              disabled={pplxKicking}
              className={`rounded-md shadow-sm active:scale-95 transition-all ${
                ['running', 'queued'].includes(pplxEnrich.job?.status)
                  ? 'text-rose-700 border-rose-300 hover:bg-rose-50'
                  : pplxEnrich.job?.status === 'error'
                    ? 'text-amber-800 border-amber-300 hover:bg-amber-50 bg-amber-50/50'
                    : 'text-violet-700 border-violet-300 hover:bg-violet-50 bg-gradient-to-r from-violet-50 to-fuchsia-50'
              }`}
              title={
                pplxEnrich.job?.status === 'running'
                  ? `Running: ${pplxEnrich.job.enriched || 0}/${pplxEnrich.job.total || pplxEnrich.qualifying} enriched · click to cancel`
                  : pplxEnrich.job?.status === 'queued'
                    ? 'Queued — starting shortly'
                    : pplxEnrich.job?.status === 'error'
                      ? (pplxEnrich.job?.error || 'Previous run failed — click to retry')
                      : (() => {
                        const b = pplxEnrich.breakdown || {};
                        const main = b.main || 0;
                        const form = b.formulation || 0;
                        const acad = b.academic || 0;
                        const dtx = b.dtx || 0;
                        const enriched = b.already_enriched || 0;
                        const total = pplxEnrich.total || 0;
                        return (
                          `Live-search the web (Perplexity Sonar) to fill website, description, HQ, ` +
                          `founded year, TA, modality, CEO, key differentiators and recent milestones.\n\n` +
                          `Scope (${pplxEnrich.qualifying} biotechs):\n` +
                          `  • Main biotech         ${main}\n` +
                          `  • Formulation/Delivery ${form}\n` +
                          `  • Academic/University  ${acad}\n` +
                          `  • DTx & Digital        ${dtx}\n\n` +
                          `Already enriched: ${enriched} of ${total} (skipped — idempotent)\n` +
                          `Existing data is never overwritten.`
                        );
                      })()
              }
              data-testid="bulk-pplx-enrich-btn"
            >
              {pplxKicking ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</>
              ) : pplxEnrich.job?.status === 'running' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin text-rose-600" />
                  Enriching {pplxEnrich.job.enriched || 0}/{pplxEnrich.job.total || pplxEnrich.qualifying}
                  {pplxEnrich.job?.total > 0 && (
                    <span className="inline-block ml-2 h-1 w-16 bg-rose-100 rounded-full overflow-hidden align-middle">
                      <span
                        className="block h-full bg-rose-500 transition-all duration-500"
                        style={{ width: `${Math.min(100, ((pplxEnrich.job.enriched || 0) / (pplxEnrich.job.total || 1)) * 100)}%` }}
                      />
                    </span>
                  )}
                  <span className="ml-2 text-[10px] opacity-60">· cancel</span>
                </>
              ) : pplxEnrich.job?.status === 'queued' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Queued · cancel</>
              ) : pplxEnrich.job?.status === 'error' ? (
                <><AlertTriangle className="w-4 h-4 mr-2 text-amber-600" />
                  Retry · {pplxEnrich.qualifying} left
                </>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2 text-violet-600" />
                  Enrich {pplxEnrich.qualifying} · therapeutic biotechs
                </>
              )}
            </Button>
            {['running', 'queued', 'error'].includes(pplxEnrich.job?.status) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetPplxEnrich}
                className="text-[10px] text-slate-500 hover:text-slate-700 h-7 px-2"
                title="Force-clear the enrichment job state (use if the spinner is stuck)"
                data-testid="reset-pplx-enrich-btn"
              >
                Reset
              </Button>
            )}
            </div>
          )}
          <Dialog open={importHistoryOpen} onOpenChange={setImportHistoryOpen}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="import-history-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-600" /> Recent .docx Imports
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 ml-auto"
                    onClick={fetchImportHistory}
                    disabled={importHistoryLoading}
                    title="Refresh"
                    data-testid="import-history-refresh"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${importHistoryLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </DialogTitle>
                <DialogDescription>
                  Last 20 bulk-enrichment jobs across all users. Click Cancel on any stuck in-flight job to force-mark it failed.
                </DialogDescription>
              </DialogHeader>
              {importHistoryLoading && importHistoryJobs.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
                </div>
              ) : importHistoryJobs.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500">No import jobs yet.</div>
              ) : (
                <div className="space-y-2">
                  {importHistoryJobs.map(j => {
                    const inFlight = ['queued', 'parsing', 'matching'].includes(j.stage);
                    const stageColor = {
                      queued: 'bg-slate-100 text-slate-700',
                      parsing: 'bg-sky-100 text-sky-700',
                      matching: 'bg-violet-100 text-violet-700',
                      done: 'bg-emerald-100 text-emerald-700',
                      failed: 'bg-rose-100 text-rose-700',
                    }[j.stage] || 'bg-slate-100 text-slate-700';
                    const s = j.summary || {};
                    const created = j.created_at ? new Date(j.created_at).toLocaleString() : '—';
                    const heartbeat = j.last_progress_at
                      ? `${Math.round((Date.now() - new Date(j.last_progress_at).getTime()) / 1000)}s ago`
                      : '—';
                    return (
                      <div key={j.id} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`import-history-row-${j.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`text-xs ${stageColor}`}>{j.stage}</Badge>
                              <span className="text-sm font-medium text-slate-800 truncate" title={j.filename}>
                                {j.filename || 'unnamed.docx'}
                              </span>
                              <span className="text-xs text-slate-500">· {j.progress || 0}%</span>
                            </div>
                            <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                              <span>by {j.user_email || '—'}</span>
                              <span>started {created}</span>
                              {inFlight && <span>last heartbeat {heartbeat}</span>}
                              {j.parse_chunks_total && (
                                <span>chunks {j.parse_chunks_done || 0}/{j.parse_chunks_total} · {j.parse_companies_found || 0} companies parsed</span>
                              )}
                            </div>
                            {j.stage === 'done' && (
                              <div className="mt-1 text-xs text-emerald-700">
                                {s.total_parsed || 0} parsed · {s.enriched_existing || 0} enriched · {s.created_new || 0} new · {s.assets_added || 0} assets · {s.contacts_added || 0} contacts
                                {Array.isArray(s.failed_chunks) && s.failed_chunks.length > 0 && (
                                  <span className="ml-2 text-amber-700 font-medium">
                                    ⚠ {s.failed_chunks.length} chunk(s) skipped
                                  </span>
                                )}
                              </div>
                            )}
                            {j.error && (
                              <div className="mt-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 break-words">
                                {j.error}
                              </div>
                            )}
                            {(j.stage === 'done' || (Array.isArray(s.failed_chunks) && s.failed_chunks.length > 0)) && (
                              <button
                                onClick={() => toggleJobDetails(j.id)}
                                className="mt-1 text-xs text-sky-600 hover:underline"
                                data-testid={`toggle-report-${j.id}`}
                              >
                                {expandedJobId === j.id ? '▲ Hide report' : '▼ View full report'}
                              </button>
                            )}
                            {expandedJobId === j.id && (
                              <div className="mt-2 border-t border-slate-200 pt-2 space-y-2">
                                {/* Failed chunks */}
                                {Array.isArray(s.failed_chunks) && s.failed_chunks.length > 0 && (
                                  <div>
                                    <div className="text-xs font-semibold text-amber-800 mb-1">
                                      ⚠ Skipped slices ({s.failed_chunks.length}) — re-upload just these to recover them:
                                    </div>
                                    <div className="space-y-1">
                                      {s.failed_chunks.map((fc, idx) => (
                                        <div key={idx} className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                          <div className="font-mono text-amber-900">chunk {fc.label} · {fc.chars} chars</div>
                                          {fc.error && <div className="text-rose-700 mt-0.5 break-words">{fc.error}</div>}
                                          <div className="text-slate-700 mt-0.5 whitespace-pre-wrap break-words">{fc.preview}…</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* Per-company details */}
                                {(() => {
                                  const full = jobDetailsCache[j.id];
                                  if (!full) return <div className="text-xs text-slate-500"><Loader2 className="w-3 h-3 inline-block animate-spin mr-1" /> loading per-company breakdown…</div>;
                                  const details = full?.summary?.details || [];
                                  if (details.length === 0) return <div className="text-xs text-slate-500">No per-company details recorded.</div>;
                                  return (
                                    <div>
                                      <div className="text-xs font-semibold text-slate-700 mb-1">Per-company breakdown ({details.length})</div>
                                      <div className="max-h-64 overflow-y-auto border border-slate-200 rounded">
                                        <table className="w-full text-xs">
                                          <thead className="bg-slate-50 sticky top-0">
                                            <tr className="text-left">
                                              <th className="px-2 py-1">Parsed</th>
                                              <th className="px-2 py-1">Matched / Action</th>
                                              <th className="px-2 py-1">Changes</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {details.map((d, di) => (
                                              <tr key={di} className="border-t border-slate-100">
                                                <td className="px-2 py-1 text-slate-800">{d.parsed_name}</td>
                                                <td className="px-2 py-1">
                                                  {d.action === 'created' ? (
                                                    <Badge className="bg-blue-100 text-blue-700 text-[10px]">NEW</Badge>
                                                  ) : (
                                                    <span>
                                                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">enriched</Badge>
                                                      <span className="ml-1 text-slate-600">{d.matched_name}</span>
                                                      {d.match_score && <span className="text-slate-400 ml-1">({d.match_score}%)</span>}
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="px-2 py-1 text-slate-500">
                                                  {d.assets_added ? `${d.assets_added} assets` : ''}
                                                  {d.assets_enriched ? `${d.assets_added ? ', ' : ''}${d.assets_enriched} assets+` : ''}
                                                  {d.contacts_added ? `${(d.assets_added || d.assets_enriched) ? ', ' : ''}${d.contacts_added} contacts` : ''}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                          {inFlight && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs text-rose-700 border-rose-300 hover:bg-rose-50 shrink-0"
                              onClick={() => cancelImportJob(j.id)}
                              disabled={cancellingJobId === j.id}
                              data-testid={`cancel-job-${j.id}`}
                            >
                              {cancellingJobId === j.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><XCircle className="w-3 h-3 mr-1" /> Cancel</>}
                            </Button>
                          )}
                          {j.stage === 'failed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50 shrink-0"
                              onClick={() => {
                                setImportHistoryOpen(false);
                                bulkDocxInputRef.current?.click();
                              }}
                              title="Re-open file picker to re-upload this .docx"
                              data-testid={`retry-job-${j.id}`}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" /> Retry
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <DialogFooter>
                <Button
                  onClick={() => setImportHistoryOpen(false)}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="import-history-done-btn"
                >
                  Done
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {/* Import CSV moved to Data Ops → all uploads centralised there (2026-02). */}
          {/* Handshake classifier moved to Data Ops → /data-ops/handshake-classifier (2026-02 v16b). */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditing(null); setForm({ ...emptyForm }); setCompanyDocs([]); setDocSlots([{ type: 'Non-Confidential Deck', file: null, fileName: '' }]); } }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-md shadow-sm active:scale-95 transition-all" data-testid="add-biotech-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editing ? 'Edit Company' : 'Add Biotech Company'}</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">Enter company details, contacts, molecules, and upload documents.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-2">
              {/* Company Info */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Company Information</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name *</Label>
                    <Input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} required data-testid="input-company-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <CountrySelect value={form.location} onChange={v => setForm(p => ({ ...p, location: v }))} testId="input-location" />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://" data-testid="input-website" />
                  </div>
                  <div className="space-y-2">
                    <Label>Therapeutic Area</Label>
                    <Input value={form.therapeutic_area} onChange={e => setForm(p => ({ ...p, therapeutic_area: e.target.value }))} placeholder="e.g. Oncology, CNS, Rare Disease" data-testid="input-therapeutic-area" />
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <Label>Company Description</Label>
                  <Textarea value={form.company_description ?? ''} onChange={e => setForm(p => ({ ...p, company_description: e.target.value }))} rows={2} placeholder="Brief description of the company and their platform" data-testid="input-company-description" />
                </div>
              </div>

              <Separator />

              {/* Status */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Status</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CDA Status</Label>
                    <Select value={form.cda_status || 'Not Started'} onValueChange={v => setForm(p => ({ ...p, cda_status: v }))}>
                      <SelectTrigger data-testid="select-cda-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {AGREEMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Partnership Agreement</Label>
                    <Select value={form.partnership_status || 'Not Started'} onValueChange={v => setForm(p => ({ ...p, partnership_status: v }))}>
                      <SelectTrigger data-testid="select-partnership-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {AGREEMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>NCD Status</Label>
                    <Select value={form.ncd_status || 'Not Received'} onValueChange={v => setForm(p => ({ ...p, ncd_status: v }))}>
                      <SelectTrigger data-testid="select-ncd-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {NCD_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Regional Flags */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Regional Licensing</p>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="india-licensing-toggle">
                  <input type="checkbox" checked={!!form.india_licensing} onChange={e => setForm(p => ({ ...p, india_licensing: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500" />
                  <span className="text-sm text-slate-700">India Licensing</span>
                  <span className="text-[10px] text-slate-400">— Target all Indian pharma contacts for this biotech</span>
                </label>
              </div>

              <Separator />

              {/* Fundraising Profile */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Fundraising Profile</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Funding Stage</Label>
                    <Select value={form.funding_stage || ''} onValueChange={v => setForm(p => ({ ...p, funding_stage: v }))}>
                      <SelectTrigger data-testid="select-funding-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                      <SelectContent>
                        {['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Bridge', 'IPO', 'Grant', 'Debt', 'Other'].map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Target Amount</Label>
                    <Input value={form.funding_amount || ''} onChange={e => setForm(p => ({ ...p, funding_amount: e.target.value }))} placeholder="e.g., $5M, $10-15M" data-testid="input-funding-amount" />
                  </div>
                  <div className="space-y-2">
                    <Label>Use of Funds</Label>
                    <Input value={form.use_of_funds || ''} onChange={e => setForm(p => ({ ...p, use_of_funds: e.target.value }))} placeholder="e.g., Phase 2 trials, scale-up" data-testid="input-use-of-funds" />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Documents Upload (up to 3) */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Documents ({docSlots.length}/5)
                  </p>
                  {docSlots.length < 5 && (
                    <Button type="button" variant="ghost" size="sm" onClick={addDocSlot} className="text-sky-600 hover:text-sky-700 h-7 text-xs" data-testid="add-doc-slot-btn">
                      <Plus className="w-3 h-3 mr-1" /> Add Document
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  {docSlots.map((slot, idx) => (
                    <div key={idx} className="relative bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-sky-50 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4 text-sky-600" />
                        </div>
                        <Select value={slot.type} onValueChange={v => updateDocSlotType(idx, v)}>
                          <SelectTrigger className="w-[200px] h-8 text-xs" data-testid={`select-doc-type-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DOC_TYPE_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <input
                          type="file"
                          ref={fileInputRefs[idx]}
                          onChange={e => handleFileSelect(idx, e)}
                          className="hidden"
                          accept=".pdf,.pptx,.ppt,.doc,.docx,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                          data-testid={`doc-file-input-${idx}`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs shrink-0"
                          onClick={() => fileInputRefs[idx]?.current?.click()}
                          data-testid={`choose-file-btn-${idx}`}
                        >
                          <Upload className="w-3 h-3 mr-1.5" />
                          {slot.fileName || 'Choose File'}
                        </Button>
                        {slot.fileName && (
                          <Badge variant="secondary" className="text-[10px] max-w-[120px] truncate">{slot.fileName}</Badge>
                        )}
                        {docSlots.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeDocSlot(idx)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-500 shrink-0 ml-auto" data-testid={`remove-doc-slot-${idx}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Previously uploaded documents — categorized */}
                {companyDocs.length > 0 && (
                  <div className="mt-3 space-y-3">
                    {[
                      { label: 'Documents', types: ['Non-Confidential Deck', 'Corporate Overview', 'Other', 'general'], icon: <FileText className="w-3.5 h-3.5 text-sky-500" /> },
                      { label: 'CDA', types: ['CDA'], icon: <FileText className="w-3.5 h-3.5 text-blue-500" /> },
                      { label: 'Partnership Agreement', types: ['Partnership Agreement'], icon: <FileText className="w-3.5 h-3.5 text-purple-500" /> },
                    ].map(section => {
                      const sectionDocs = companyDocs.filter(d => section.types.includes(d.doc_type) || (!d.doc_type && section.label === 'Documents'));
                      if (sectionDocs.length === 0) return null;
                      return (
                        <div key={section.label}>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            {section.icon}
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{section.label} ({sectionDocs.length})</p>
                          </div>
                          {sectionDocs.map((doc) => (
                            <div key={doc.id} className="flex items-center gap-3 px-3 py-2 rounded-md bg-white border border-slate-100 mb-1">
                              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-800 truncate">{doc.original_filename}</p>
                                <Badge variant="secondary" className="text-[10px] mt-0.5">{doc.doc_type}</Badge>
                              </div>
                              <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" onClick={() => setDeleteDocTarget(doc)} data-testid={`remove-doc-${doc.id}`}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Separator />

              {/* Contacts */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Contacts ({form.contacts.length})
                  </p>
                  <Button type="button" variant="ghost" size="sm" onClick={addContact} className="text-sky-600 hover:text-sky-700 h-7 text-xs" data-testid="add-contact-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Contact
                  </Button>
                </div>
                <div className="space-y-4">
                  {form.contacts.map((contact, idx) => (
                    <div key={idx} className="relative bg-slate-50 rounded-lg p-4 border border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-xs font-medium text-slate-600">Contact {idx + 1}</span>
                        </div>
                        {form.contacts.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeContact(idx)} className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" data-testid={`remove-contact-${idx}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="relative">
                          <Input placeholder="Full Name" value={contact.name} onChange={e => { updateContact(idx, 'name', e.target.value); setContactSuggestIndex(idx); }} onFocus={() => setContactSuggestIndex(idx)} onBlur={() => setTimeout(() => setContactSuggestIndex(null), 200)} className="h-9 text-sm" data-testid={`input-contact-name-${idx}`} />
                          {contactSuggestIndex === idx && getContactSuggestions(contact.name).length > 0 && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid={`contact-suggestions-${idx}`}>
                              {getContactSuggestions(contact.name).map((s, si) => (
                                <button key={si} type="button" onMouseDown={(e) => { e.preventDefault(); selectContactSuggestion(idx, s); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
                                  <p className="text-sm font-medium text-slate-800">{s.name}</p>
                                  <p className="text-xs text-slate-500">{[s.designation, s.company_name].filter(Boolean).join(' · ')}</p>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <Input placeholder="Designation (e.g. CEO, CSO)" value={contact.designation} onChange={e => updateContact(idx, 'designation', e.target.value)} className="h-9 text-sm" data-testid={`input-contact-designation-${idx}`} />
                        <Input placeholder="Email" value={contact.email} onChange={e => updateContact(idx, 'email', e.target.value)} className="h-9 text-sm" data-testid={`input-contact-email-${idx}`} />
                        <Input placeholder="Phone" value={contact.phone} onChange={e => updateContact(idx, 'phone', e.target.value)} className="h-9 text-sm" data-testid={`input-contact-phone-${idx}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Assets */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Assets {form.molecules.length > 1 ? `(${form.molecules.length} molecules)` : ''}
                  </p>
                  <Button type="button" variant="ghost" size="sm" onClick={addMolecule} className="text-sky-600 hover:text-sky-700 h-7 text-xs" data-testid="add-molecule-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Molecule
                  </Button>
                </div>
                <div className="space-y-4">
                  {form.molecules.map((mol, idx) => (
                    <div key={idx} className="relative bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
                      {form.molecules.length > 1 && (
                        <div className="flex items-center justify-between px-4 py-2 bg-slate-100/80 border-b border-slate-200">
                          <div className="flex items-center gap-2">
                            <FlaskConical className="w-3.5 h-3.5 text-violet-500" />
                            <span className="text-xs font-medium text-slate-600">Molecule {idx + 1}</span>
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeMolecule(idx)} className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" data-testid={`remove-molecule-${idx}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                      <div className="divide-y divide-slate-200">
                        {/* Molecule Name */}
                        <div className="grid grid-cols-[180px_1fr] items-center px-4 py-2.5 hover:bg-white/50 transition-colors">
                          <span className="text-sm font-medium text-slate-700">Molecule/Product Number</span>
                          <Input
                            placeholder="Enter molecule/product number..."
                            value={mol['Molecule/Product Number'] || ''}
                            onChange={e => updateMolecule(idx, 'Molecule/Product Number', e.target.value)}
                            className="h-8 text-sm border-slate-200"
                            data-testid={`input-param-molecule-product-number-${idx}`}
                          />
                        </div>
                        {/* Development Stage right below name */}
                        <div className="grid grid-cols-[180px_1fr] items-center px-4 py-2.5 hover:bg-white/50 transition-colors">
                          <span className="text-sm font-medium text-slate-700">Development Stage</span>
                          <div className="flex gap-2">
                            <Select value={mol.stage || 'Pre-Clinical'} onValueChange={v => updateMolecule(idx, 'stage', v)}>
                              <SelectTrigger className="h-8 text-sm w-[160px]" data-testid={`select-mol-stage-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Input
                              placeholder="Stage notes..."
                              value={mol.stage_notes || ''}
                              onChange={e => updateMolecule(idx, 'stage_notes', e.target.value)}
                              className="h-8 text-sm border-slate-200 flex-1"
                              data-testid={`input-mol-stage-notes-${idx}`}
                            />
                          </div>
                        </div>
                        {/* Remaining parameters */}
                        {PARAMETER_FIELDS.filter(f => f !== 'Molecule/Product Number').map((field) => (
                          <div key={field} className="grid grid-cols-[180px_1fr] items-center px-4 py-2.5 hover:bg-white/50 transition-colors">
                            <span className="text-sm font-medium text-slate-700">{field}</span>
                            <Input
                              placeholder={`Enter ${field.toLowerCase()}...`}
                              value={mol[field] || ''}
                              onChange={e => updateMolecule(idx, field, e.target.value)}
                              className="h-8 text-sm border-slate-200"
                              data-testid={`input-param-${field.toLowerCase().replace(/[\s/]/g, '-')}-${idx}`}
                            />
                          </div>
                        ))}
                        {/* Per-molecule: Description, Objectives, Looking For, Outreach Status */}
                        <div className="px-4 py-3 bg-white/30 space-y-3 border-t border-slate-200">
                          <div className="space-y-1.5">
                            <span className="text-xs font-medium text-slate-600">Asset Description</span>
                            <Textarea
                              value={mol.molecule_description || ''}
                              onChange={e => updateMolecule(idx, 'molecule_description', e.target.value)}
                              rows={2}
                              placeholder="Description of this molecule, asset, or technology"
                              className="text-sm border-slate-200"
                              data-testid={`input-mol-description-${idx}`}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <span className="text-xs font-medium text-slate-600">What They Are Trying to Achieve</span>
                            <Textarea
                              value={mol.objectives || ''}
                              onChange={e => updateMolecule(idx, 'objectives', e.target.value)}
                              rows={2}
                              placeholder="e.g. Out-licensing for Phase 2, seeking co-development partner"
                              className="text-sm border-slate-200"
                              data-testid={`input-mol-objectives-${idx}`}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                              <span className="text-xs font-medium text-slate-600">Looking For</span>
                              <div className="space-y-1.5 pt-0.5">
                                {DEAL_INTEREST_OPTIONS.map(opt => {
                                  const interests = mol.deal_interests || [];
                                  const checked = interests.includes(opt.value);
                                  return (
                                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          const next = checked ? interests.filter(v => v !== opt.value) : [...interests, opt.value];
                                          updateMolecule(idx, 'deal_interests', next);
                                        }}
                                        className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                        data-testid={`check-mol-${opt.value.toLowerCase().replace(/[^a-z]/g, '-')}-${idx}`}
                                      />
                                      <span className="text-xs text-slate-700">{opt.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <span className="text-xs font-medium text-slate-600">Emailing</span>
                              <Select value={mol.emailing_status || 'Not Started'} onValueChange={v => updateMolecule(idx, 'emailing_status', v)}>
                                <SelectTrigger className="h-8 text-sm" data-testid={`select-mol-emailing-status-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {EMAILING_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-biotech-btn">
                  {editing ? 'Update Company' : 'Add Company'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Duplicate detection banner + expandable review panel (disabled Jul 2026) */}
      {SHOW_DUP_BANNER && dupTotal > 0 && (
        <Card className="border border-rose-200 bg-rose-50/50 shadow-sm mb-4" data-testid="dup-banner">
          <CardContent className="p-3">
            <button
              onClick={() => setDupOpen(o => !o)}
              className="w-full flex items-center justify-between gap-3 text-left"
              data-testid="dup-banner-toggle"
            >
              <div className="flex items-center gap-2 min-w-0">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                <span className="text-sm font-semibold text-rose-900">
                  {dupTotal} potential duplicate{dupTotal !== 1 ? 's' : ''} detected
                </span>
                {dupMergedIds.size > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                    {dupMergedIds.size} merged
                  </Badge>
                )}
                <span className="text-[11px] text-rose-700/70 truncate">
                  — click to review and merge inline
                </span>
              </div>
              {dupOpen
                ? <ChevronUp className="w-4 h-4 text-rose-600 shrink-0" />
                : <ChevronDown className="w-4 h-4 text-rose-600 shrink-0" />
              }
            </button>

            {dupOpen && (
              <div className="mt-3 space-y-2 max-h-[520px] overflow-y-auto pr-1">
                {dupPairs.length > 0 && (() => {
                  const selectableKeys = dupPairs
                    .map(p => `${p.a.id}::${p.b.id}`)
                    .filter(k => !dupMergedIds.has(k));
                  const allSelected = selectableKeys.length > 0 && dupSelected.size === selectableKeys.length;
                  const someSelected = dupSelected.size > 0 && !allSelected;
                  return (
                    <div className="sticky top-0 z-10 bg-white border border-slate-200 rounded-md p-2 flex items-center gap-3 flex-wrap" data-testid="dup-bulk-bar">
                      <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-slate-700 font-medium">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = someSelected; }}
                          onChange={toggleDupSelectAll}
                          className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                          data-testid="dup-select-all"
                        />
                        Select all ({selectableKeys.length})
                      </label>
                      <span className="text-xs text-slate-500">
                        {dupSelected.size > 0 ? `${dupSelected.size} selected` : 'none selected'}
                      </span>
                      <div className="ml-auto flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-3 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                          onClick={handleDismissAll}
                          disabled={dupBulkRunning || selectableKeys.length === 0}
                          title="One-click: mark every currently-shown pair as not a duplicate"
                          data-testid="dup-dismiss-all-btn"
                        >
                          Dismiss all ({selectableKeys.length})
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-xs"
                          onClick={() => setDupBulkDismissConfirm(true)}
                          disabled={dupSelected.size === 0 || dupBulkRunning}
                          data-testid="dup-bulk-dismiss-btn"
                        >
                          <X className="w-3 h-3 mr-1" /> Not duplicates ({dupSelected.size})
                        </Button>
                        <Button
                          size="sm"
                          className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-3 text-xs"
                          onClick={() => setDupBulkMergeConfirm(true)}
                          disabled={dupSelected.size === 0 || dupBulkRunning}
                          data-testid="dup-bulk-merge-btn"
                        >
                          {dupBulkRunning
                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Working…</>
                            : <>Merge selected ({dupSelected.size})</>}
                        </Button>
                      </div>
                    </div>
                  );
                })()}
                {dupAutoMerged.length > 0 && (
                  <div className="border border-emerald-200 bg-emerald-50 rounded-md p-2.5" data-testid="auto-merged-banner">
                    <div className="text-xs font-semibold text-emerald-800 mb-1.5 flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" />
                      Auto-merged {dupAutoMerged.length} pair{dupAutoMerged.length !== 1 ? 's' : ''} — same company by domain or shared asset
                    </div>
                    <div className="space-y-1">
                      {dupAutoMerged.map((m, i) => (
                        <div key={i} className="text-[11px] text-emerald-900 bg-white border border-emerald-100 rounded px-2 py-1">
                          <strong>{m.dropped_name}</strong> → <strong>{m.survivor_name}</strong>
                          <span className="text-emerald-700/70 ml-1">
                            {m.reason === 'shared_asset'
                              ? <>· shared asset: <em>{m.shared_asset}</em></>
                              : <>· @{(m.shared_domains || []).join(', @')}</>
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {dupPairs.map((p, idx) => {
                  const pairKey = `${p.a.id}::${p.b.id}`;
                  const done = dupMergedIds.has(pairKey);
                  return (
                    <DupPairRow
                      key={pairKey}
                      pair={p}
                      pairKey={pairKey}
                      idx={idx}
                      done={done}
                      merging={dupMerging === pairKey || dupBulkRunning}
                      selected={dupSelected.has(pairKey)}
                      onToggleSelect={() => toggleDupSelected(pairKey)}
                      onMergeClick={(survivor, drop) => setDupConfirm({ pair: p, pairKey, survivor, drop })}
                      onDismissClick={() => setDupDismissConfirm({ pair: p, pairKey })}
                    />
                  );
                })}
                {dupLoading && (
                  <div className="text-center text-xs text-rose-600/70 py-2">
                    <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Refreshing…
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirm merge dialog */}
      <Dialog open={!!dupConfirm} onOpenChange={(open) => { if (!open) setDupConfirm(null); }}>
        <DialogContent className="max-w-lg" data-testid="dup-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm merge</DialogTitle>
            <DialogDescription>
              Non-destructive and audit-logged. Not automatically reversible.
            </DialogDescription>
          </DialogHeader>
          {dupConfirm && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-emerald-700">
                <Check className="w-4 h-4" />
                <span>Keep: <strong>{dupConfirm.survivor.company_name}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-rose-700">
                <Trash2 className="w-4 h-4" />
                <span>Drop: <strong>{dupConfirm.drop.company_name}</strong></span>
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 rounded p-3 space-y-1">
                <p>• Assets, outreach, contacts, documents, campaigns from drop → survivor</p>
                <p>• Empty survivor fields filled from drop (no overwrites)</p>
                <p>• Drop record permanently deleted after move (audit snapshot saved)</p>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setDupConfirm(null)}
                  data-testid="dup-cancel-btn">Cancel</Button>
                <Button
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={() => handleMerge(dupConfirm.survivor.id, dupConfirm.drop.id, dupConfirm.pairKey)}
                  disabled={dupMerging !== null}
                  data-testid="dup-confirm-btn"
                >
                  {dupMerging !== null
                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Merging…</>
                    : 'Merge'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!dupDismissConfirm} onOpenChange={(open) => { if (!open) setDupDismissConfirm(null); }}>
        <AlertDialogContent data-testid="dup-dismiss-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as not a duplicate?</AlertDialogTitle>
            <AlertDialogDescription>
              {dupDismissConfirm && (
                <>
                  <strong>{dupDismissConfirm.pair.a.company_name}</strong> and <strong>{dupDismissConfirm.pair.b.company_name}</strong> will be treated as distinct companies. This pair won't reappear in the duplicate banner.
                  <br /><br />
                  You can undo this later via the admin DB if needed. Continue?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dup-dismiss-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (dupDismissConfirm) {
                  handleDismissDuplicate(
                    dupDismissConfirm.pair.a.id,
                    dupDismissConfirm.pair.b.id,
                    dupDismissConfirm.pairKey
                  );
                }
              }}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="dup-dismiss-confirm-btn"
            >
              Yes, not a duplicate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk merge confirm */}
      <AlertDialog open={dupBulkMergeConfirm} onOpenChange={setDupBulkMergeConfirm}>
        <AlertDialogContent data-testid="dup-bulk-merge-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Merge {dupSelected.size} pair(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              For each pair, the server auto-picks the survivor using the same heuristic as auto-merge
              (more contacts → older record wins). Assets, outreach, contacts and documents are moved to
              the survivor; the other record is deleted (audit snapshot saved). Not automatically reversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dup-bulk-merge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkMerge}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="dup-bulk-merge-confirm-btn"
            >
              Merge {dupSelected.size} pair(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk dismiss confirm */}
      <AlertDialog open={dupBulkDismissConfirm} onOpenChange={setDupBulkDismissConfirm}>
        <AlertDialogContent data-testid="dup-bulk-dismiss-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {dupSelected.size} pair(s) as not duplicates?</AlertDialogTitle>
            <AlertDialogDescription>
              These pairs will be treated as distinct companies and won't reappear in the duplicate banner.
              You can undo this later via the admin DB if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dup-bulk-dismiss-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDismiss}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="dup-bulk-dismiss-confirm-btn"
            >
              Yes, not duplicates
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Extraction Progress */}
      {extractionStatus?.running && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="extraction-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
              <p className="text-xs font-medium text-amber-800">Extracting assets: {extractionStatus.current_company || '...'}</p>
            </div>
            <p className="text-[10px] text-amber-600">
              {extractionStatus.completed}/{extractionStatus.total} · {extractionStatus.extracted} extracted · {extractionStatus.errors} errors
            </p>
          </div>
          <div className="w-full bg-amber-200 rounded-full h-1.5">
            <div className="bg-amber-600 h-1.5 rounded-full transition-all duration-500" style={{ width: `${extractionStatus.total > 0 ? (extractionStatus.completed / extractionStatus.total * 100) : 0}%` }} />
          </div>
        </div>
      )}

      {/* Source Toggle + Search & Filter */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex bg-slate-100 rounded-md p-0.5 mr-1" data-testid="source-filter-toggle">
          <button type="button" onClick={() => handleSourceFilterChange('manual')}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded transition-colors ${sourceFilter === 'manual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="filter-my-clients">My Companies ({sourceCounts.manual})</button>
          <button type="button" onClick={() => handleSourceFilterChange('bio_international')}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded transition-colors ${sourceFilter === 'bio_international' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="filter-database">Database ({sourceCounts.database})</button>
          <button type="button" onClick={() => handleSourceFilterChange('all')}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded transition-colors ${sourceFilter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="filter-all">All ({sourceCounts.all})</button>
        </div>
        {/* Feb 2026 · Category filter (Formulation / NDDS, DTx, University,
            Platform). Drives the `category` URL param which the backend
            translates into the appropriate categories-array match. */}
        <select
          value={category || ''}
          onChange={(e) => {
            const sp = new URLSearchParams(searchParams);
            const v = e.target.value;
            if (v) sp.set('category', v); else sp.delete('category');
            setSearchParams(sp, { replace: true });
          }}
          className="px-2 py-1.5 text-[11px] font-medium rounded-md border border-slate-300 bg-white cursor-pointer hover:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
          data-testid="category-filter"
          title="Filter Out-Licensers by category. Formulation (NDDS) = novel drug delivery system companies."
        >
          <option value="">All categories</option>
          <option value="ndds">Formulation · Delivery (NDDS)</option>
          <option value="platform">Platform · Discovery</option>
          <option value="dtx">DTx · Digital Health</option>
          <option value="university">Academic · Translational</option>
        </select>
        {sourceFilter === 'bio_international' && (
          <Button variant="outline" size="sm"
            className="border-amber-200 text-amber-700 hover:bg-amber-50 h-8 text-[11px]"
            onClick={startAssetExtraction}
            disabled={extractionStatus?.running}
            data-testid="extract-assets-btn"
          >
            {extractionStatus?.running ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1.5" />}
            {extractionStatus?.running ? `Extracting ${extractionStatus.completed}/${extractionStatus.total}` : 'Extract Assets from Profiles'}
          </Button>
        )}
        <select
          value={agreementFilter}
          onChange={e => setAgreementFilter(e.target.value)}
          className={`px-2 py-1.5 text-[11px] font-medium rounded-md border transition-colors appearance-none cursor-pointer ${
            agreementFilter
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
          data-testid="filter-partnership"
        >
          <option value="">Partnership</option>
          {AGREEMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {/* Multi-select Stage chips — OR logic. Replaces the old
            single-value "min_stage" dropdown so cohort blasts can
            segment across arbitrary stage buckets (e.g. "Phase 2 OR
            Phase 3 OR Filed" for a de-risked partnering blast). Uses
            a popover for compactness — the count chip shows the
            selection state at a glance. */}
        <Popover open={stagesPopoverOpen} onOpenChange={setStagesPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`pl-2 pr-2 py-1.5 text-[11px] font-medium rounded-md border transition-colors flex items-center gap-1 ${
                stagesFilter.length > 0
                  ? 'bg-violet-50 border-violet-300 text-violet-700'
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
              data-testid="filter-stages"
              title="Select one or more asset stages — OR logic. E.g. Phase 2 + Phase 3 shows companies with an asset at EITHER stage."
            >
              {stagesFilter.length === 0
                ? <>Any Stage <ChevronDown className="w-3 h-3" /></>
                : <>{stagesFilter.length} stage{stagesFilter.length > 1 ? 's' : ''} <ChevronDown className="w-3 h-3" /></>}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-52 p-2" data-testid="stages-popover">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold px-1 pb-1.5">Include stages</div>
            {[
              { value: 'Preclinical', label: 'Preclinical' },
              { value: 'IND', label: 'IND' },
              { value: 'Phase 1', label: 'Phase 1' },
              { value: 'Phase 2', label: 'Phase 2' },
              { value: 'Phase 3', label: 'Phase 3' },
              { value: 'Filed', label: 'Filed / Registered' },
              { value: 'Approved', label: 'Approved / Marketed' },
            ].map(opt => {
              const checked = stagesFilter.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-[12px] ${checked ? 'bg-violet-50 text-violet-800' : 'hover:bg-slate-50 text-slate-700'}`}
                  data-testid={`stage-opt-${opt.value.toLowerCase().replace(/\s+/g,'-')}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = checked
                        ? stagesFilter.filter(s => s !== opt.value)
                        : [...stagesFilter, opt.value];
                      setStagesFilter(next);
                      const sp = new URLSearchParams(searchParams);
                      if (next.length > 0) sp.set('stages', next.join(',')); else sp.delete('stages');
                      setSearchParams(sp, { replace: true });
                      fetchCompanies(1, search, sourceFilter, stageFilter, orphanFilter, emailFilter, minStageFilter, taFilter, modalityFilter);
                    }}
                    className="w-3.5 h-3.5 accent-violet-600"
                  />
                  <span className="text-[12px]">{opt.label}</span>
                </label>
              );
            })}
            {stagesFilter.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setStagesFilter([]);
                  const sp = new URLSearchParams(searchParams);
                  sp.delete('stages');
                  setSearchParams(sp, { replace: true });
                  fetchCompanies(1, search, sourceFilter, stageFilter, orphanFilter, emailFilter, minStageFilter, taFilter, modalityFilter);
                }}
                className="mt-1.5 w-full text-center text-[11px] text-slate-500 hover:text-slate-800 py-1 border-t border-slate-100"
                data-testid="stages-clear"
              >
                Clear stages
              </button>
            )}
          </PopoverContent>
        </Popover>
        {/* Advanced Filters toggle — surfaces TA / Modality / Exclude-Met
            behind a chevron so the primary toolbar stays campaign-focused.
            Auto-opens if any advanced filter has a value on load. */}
        <button
          type="button"
          onClick={() => setAdvancedOpen(v => !v)}
          className={`px-2 py-1.5 text-[11px] font-medium rounded-md border transition-colors flex items-center gap-1 ${
            (taFilter || modalityFilter || cohortExcludeMet)
              ? 'bg-sky-50 border-sky-300 text-sky-700'
              : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
          data-testid="advanced-filters-toggle"
          title="Show / hide advanced filters: therapeutic area, modality, exclude met-in-person"
        >
          Advanced {advancedOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {(taFilter || modalityFilter || cohortExcludeMet) && <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-sky-500" />}
        </button>
        {advancedOpen && (
          <>
        {/* Therapeutic Area filter — free-text substring across
            asset.therapeutic_area / indication / target. Debounced 400ms
            so typing "onc" doesn't fire a request per keystroke. */}
        <DebouncedFilterInput
          value={taFilter}
          placeholder="Therapeutic area…"
          testId="filter-ta"
          activeColor="emerald"
          onCommit={(v) => {
            setTaFilter(v);
            const sp = new URLSearchParams(searchParams);
            if (v) sp.set('ta', v); else sp.delete('ta');
            setSearchParams(sp, { replace: true });
            fetchCompanies(1, search, sourceFilter, stageFilter, orphanFilter, emailFilter, minStageFilter, v, modalityFilter);
          }}
        />
        {/* Modality filter — free-text substring across asset.modality /
            platform / drug_delivery_mechanism. Free-text because the data
            is very messy (500+ distinct modality strings in DB). */}
        <DebouncedFilterInput
          value={modalityFilter}
          placeholder="Modality…"
          testId="filter-modality"
          activeColor="sky"
          onCommit={(v) => {
            setModalityFilter(v);
            const sp = new URLSearchParams(searchParams);
            if (v) sp.set('modality', v); else sp.delete('modality');
            setSearchParams(sp, { replace: true });
            fetchCompanies(1, search, sourceFilter, stageFilter, orphanFilter, emailFilter, minStageFilter, taFilter, v);
          }}
        />
          </>
        )}
        <button
          type="button"
          onClick={() => setDncFilter(!dncFilter)}
          className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md border transition-colors flex items-center gap-1 ${
            dncFilter
              ? 'bg-red-50 border-red-300 text-red-700'
              : 'bg-white border-slate-200 text-slate-500 hover:bg-red-50 hover:border-red-200'
          }`}
          title="Show companies marked Do Not Contact"
          data-testid="filter-dnc"
        >
          <span className="text-red-500">⊘</span> Do Not Contact ({dncCount})
        </button>
        <button
          type="button"
          onClick={() => {
            const v = !pendingOutreachFilter;
            setPendingOutreachFilter(v);
            const sp = new URLSearchParams(searchParams);
            if (v) sp.set('filter', 'pending_outreach'); else { if (sp.get('filter') === 'pending_outreach') sp.delete('filter'); }
            setSearchParams(sp, { replace: true });
          }}
          className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md border transition-colors flex items-center gap-1 ${
            pendingOutreachFilter
              ? 'bg-amber-100 border-amber-400 text-amber-900 ring-1 ring-amber-300'
              : 'bg-white border-slate-200 text-slate-500 hover:bg-amber-50 hover:border-amber-200'
          }`}
          title="Biotechs with freehand campaign rights but outreach not started yet (≥1 'Not Started' asset, has contact email, not DNC)"
          data-testid="filter-pending-outreach"
        >
          <span className={pendingOutreachFilter ? 'text-amber-700' : 'text-amber-500'}>⏳</span>
          Pending Outreach ({pendingOutreachCount})
        </button>
        {/* Email-presence cycle pill: All → Has Email → No Email → All.
            Drives the server-side `email_filter` query param and mirrors
            into ?email=has|none so the filtered list is shareable. The
            "Has Email" mode is the canonical export filter for the
            Viatris hand-off; "No Email" surfaces enrichment candidates. */}
        <button
          type="button"
          onClick={() => {
            const next = emailFilter === 'all' ? 'has' : emailFilter === 'has' ? 'none' : 'all';
            handleEmailFilterChange(next);
          }}
          className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md border transition-colors flex items-center gap-1 ${
            emailFilter === 'has'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-800 ring-1 ring-emerald-200'
              : emailFilter === 'none'
              ? 'bg-orange-50 border-orange-300 text-orange-800 ring-1 ring-orange-200'
              : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
          title={
            emailFilter === 'has'
              ? 'Showing only biotechs with at least one valid email — click for No-Email mode'
              : emailFilter === 'none'
              ? 'Showing only biotechs with NO email anywhere — click to clear filter'
              : 'Filter by email presence — click to cycle: All → Has Email → No Email'
          }
          data-testid="filter-email-presence"
        >
          <Mail className="w-3 h-3" />
          {emailFilter === 'has' ? `Has Email (${totalCount.toLocaleString()})` :
           emailFilter === 'none' ? `No Email (${totalCount.toLocaleString()})` :
           'Email'}
        </button>
        {/* Conference cohort filter — for bulk-emailing a specific conference cohort */}
        <select
          value={cohortFilter}
          onChange={e => {
            const v = e.target.value;
            setCohortFilter(v);
            const sp = new URLSearchParams(searchParams);
            if (v) sp.set('cohort', v); else sp.delete('cohort');
            setSearchParams(sp, { replace: true });
          }}
          className={`text-[11px] font-medium rounded-md border px-2 py-1.5 transition-colors ${
            cohortFilter
              ? 'bg-violet-50 border-violet-300 text-violet-700'
              : 'bg-white border-slate-200 text-slate-500'
          }`}
          data-testid="filter-cohort"
        >
          <option value="">Cohort: All</option>
          {availableCohorts.map(c => (
            <option key={c.tag} value={c.tag}>{c.label} ({c.biotech_count})</option>
          ))}
        </select>
        {cohortFilter && (
          <button
            type="button"
            onClick={() => renameCohort(cohortFilter)}
            className="text-[10px] px-1.5 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            title="Rename or merge this cohort tag into another (consolidates BIO International (2) → BIO International, etc.)"
            data-testid="cohort-rename-btn"
          >
            ✎ rename
          </button>
        )}
        {cohortFilter && (
          <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={cohortExcludeMet}
              onChange={e => {
                setCohortExcludeMet(e.target.checked);
                const sp = new URLSearchParams(searchParams);
                if (e.target.checked) sp.set('cohort_excl_met', '1'); else sp.delete('cohort_excl_met');
                setSearchParams(sp, { replace: true });
              }}
              className="w-3 h-3 accent-violet-600"
              data-testid="filter-cohort-exclude-met"
            />
            Exclude Met In-Person
          </label>
        )}
        {cohortFilter && cohortMeta && (
          <div
            className="text-[11px] text-violet-800 bg-violet-50 border border-violet-200 rounded-md px-2.5 py-1 flex items-center gap-3 flex-wrap"
            data-testid="cohort-summary"
            title={`Cohort total: ${cohortMeta.total} · With email: ${cohortMeta.withEmail} · No-email skipped: ${cohortMeta.excludedNoEmail} · Met-in-person excluded: ${cohortMeta.excludedMet}`}
          >
            {cohortLoading ? 'Loading…' : (
              <>
                <span className="flex items-center gap-1">
                  <span className="text-[9px] uppercase tracking-wide text-violet-500 font-semibold">Cohort</span>
                  <span className="font-bold text-violet-900">{cohortMeta.total.toLocaleString()}</span>
                </span>
                <span className="w-px h-3 bg-violet-200" />
                <span className="flex items-center gap-1">
                  <span className="text-[9px] uppercase tracking-wide text-emerald-600 font-semibold">Ready to email</span>
                  <span className="font-bold text-emerald-700">{cohortMeta.withEmail.toLocaleString()}</span>
                </span>
                {cohortMeta.excludedNoEmail > 0 && (
                  <>
                    <span className="w-px h-3 bg-violet-200" />
                    <span className="flex items-center gap-1 text-slate-500">
                      <span className="text-[9px] uppercase tracking-wide font-semibold">No email</span>
                      <span className="font-semibold">{cohortMeta.excludedNoEmail.toLocaleString()}</span>
                    </span>
                  </>
                )}
                {cohortMeta.excludedMet > 0 && (
                  <>
                    <span className="w-px h-3 bg-violet-200" />
                    <span className="flex items-center gap-1 text-slate-500">
                      <span className="text-[9px] uppercase tracking-wide font-semibold">Met-in-person</span>
                      <span className="font-semibold">{cohortMeta.excludedMet.toLocaleString()}</span>
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        )}
        {/* Live "match your filters" pill — only shown when EXTRA filters
            (min_stage / TA / modality / etc.) actually reduce the count
            below the cohort's with-email total. Uses the backend-computed
            all_matching_ids so it's exact regardless of pagination. */}
        {cohortFilter && cohortIds && !cohortLoading && allMatchingIds && allMatchingIds.length !== cohortMeta?.withEmail && (
          <span
            className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-md px-2.5 py-1.5 font-semibold flex items-center gap-1"
            data-testid="cohort-filtered-count"
            title="After applying min-stage / TA / modality / search filters"
          >
            <Mail className="w-3 h-3" />
            {allMatchingIds.length} match filters
          </span>
        )}
        {cohortFilter && cohortIds && cohortIds.size > 0 && !cohortLoading && (
          <button
            type="button"
            onClick={() => {
              // Backend-first selection: `allMatchingIds` is the FULL
              // id list matching cohort ∩ every applied filter,
              // computed server-side and returned in the fetch. Never
              // depends on how many rows have rendered/scrolled into
              // view. Falls back to cohortIds only if the backend list
              // isn't available (e.g. slim=0 fetch).
              const backendIds = allMatchingIds && allMatchingIds.length > 0
                ? allMatchingIds
                : Array.from(cohortIds);
              const idsToSelect = new Set(backendIds);
              setSelectedCompanies(idsToSelect);
              const hasFilters = !!(minStageFilter || taFilter || modalityFilter || dncFilter || pendingOutreachFilter || agreementFilter || emailingFilter || searchInput);
              toast.success(`Selected ${idsToSelect.size.toLocaleString()} biotech${idsToSelect.size === 1 ? '' : 's'}${hasFilters ? ' matching your filters' : ' with email in cohort'} — open Bulk Email to send`);
            }}
            className="text-[11px] font-medium rounded-md border bg-violet-600 border-violet-600 text-white hover:bg-violet-700 px-2.5 py-1.5 transition-colors flex items-center gap-1"
            data-testid="cohort-select-all"
            title="Select every biotech that matches your active filters (cohort + phase + TA + modality). Uses the backend id list — works regardless of pagination or infinite scroll."
          >
            <Check className="w-3 h-3" />
            Select all {((allMatchingIds && allMatchingIds.length > 0) ? allMatchingIds.length : cohortIds.size).toLocaleString()}
          </button>
        )}
        {(orphanFilter || agreementFilter || emailingFilter || dncFilter || pendingOutreachFilter || cohortFilter || searchInput || minStageFilter || taFilter || modalityFilter) && (
          <button
            onClick={() => {
              setOrphanFilter(false);
              setAgreementFilter('');
              setEmailingFilter('');
              setDncFilter(false);
              setPendingOutreachFilter(false);
              setCohortFilter('');
              setCohortExcludeMet(false);
              setSearchInput('');
              setSearch('');
              setMinStageFilter('');
              setTaFilter('');
              setModalityFilter('');
              setSearchParams({});
              setCurrentPage(1);
              fetchCompanies(1, '', sourceFilter, '', false, '', '', '', '');
            }}
            className="px-2 py-1.5 text-[11px] font-medium rounded-md border bg-slate-100 border-slate-300 text-slate-600 hover:bg-slate-200 transition-colors flex items-center gap-1"
            data-testid="clear-all-filters"
          >
            Clear all <span className="text-slate-400">&times;</span>
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">{search || activeFilter ? 'No companies match your search' : 'No biotech companies added yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" style={{tableLayout:'fixed'}} data-testid="biotech-table">
              <colgroup>
                <col style={{width:'28px'}} />
                <col style={{width:'22px'}} />
                <col style={{width:'22%'}} />
                <col style={{width:'34%'}} />
                <col style={{width:'12%'}} />
                <col style={{width:'14%'}} />
                <col style={{width:'110px'}} />
              </colgroup>
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="py-2 pl-2">
                    <input type="checkbox" checked={filtered.length > 0 && selectedCompanies.size === filtered.length}
                      onChange={toggleSelectAll} className="rounded border-slate-300 cursor-pointer" data-testid="select-all-companies" />
                  </th>
                  <th className="py-2 px-0"><Star className="w-3 h-3 text-slate-400" /></th>
                  <th className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 text-left">Company</th>
                  <th className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 text-left">Assets & Activity</th>
                  <th className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 text-left">NCD</th>
                  <th className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 text-left">Partnership</th>
                  <th className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => {
                  const companyAssets = allAssets.filter(a => a.company_id === c.id && !a.is_hidden).sort(sortByStage);
                  return (
                    <tr key={c.id} className={`hover:bg-slate-50/50 transition-colors ${selectedCompanies.has(c.id) ? 'bg-sky-50/30' : ''}`} data-testid={`biotech-row-${c.id}`}>
                      <td className="py-2 pl-2 align-top">
                        <input type="checkbox" checked={selectedCompanies.has(c.id)} onChange={() => toggleSelectCompany(c.id)}
                          onClick={(e) => e.stopPropagation()} className="rounded border-slate-300 cursor-pointer" data-testid={`select-company-${c.id}`} />
                      </td>
                      <td className="py-2 px-0 align-top">
                        <button onClick={(e) => { e.stopPropagation(); toggleMyCompany(c.id); }}
                          className="hover:bg-amber-50 rounded p-0.5" data-testid={`star-company-${c.id}`}>
                          <Star className={`w-3.5 h-3.5 ${c.is_my_company ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-300'}`} />
                        </button>
                      </td>
                      <td className="py-2 pr-2 align-top">
                        <a href={`/biotech/${c.id}`} target="_blank" rel="noopener noreferrer"
                          className="text-xs font-semibold text-slate-900 hover:text-violet-700 hover:underline block truncate"
                          data-testid={`link-biotech-${c.id}`}>{c.company_name}</a>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          {c.location && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{c.location}</span>}
                          {c.is_orphan_rare && <span className="text-[7px] font-medium text-rose-600 bg-rose-50 border border-rose-200 px-1 py-0 rounded" data-testid={`orphan-badge-${c.id}`}>Rare/Orphan</span>}
                          {c.india_licensing && <span className="text-base" data-testid={`india-badge-${c.id}`} title="India Licensing">🇮🇳</span>}
                          {c.extraction_status === 'no_assets' && <span className="text-[7px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1 py-0 rounded" data-testid={`no-assets-badge-${c.id}`}>No Assets</span>}
                          {isPendingOutreach(c) && (
                            <span
                              className="text-[8px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-300 px-1 py-0 rounded flex items-center gap-0.5"
                              title="Freehand to email — has Not-Started assets + contact email on file. Click the chip above to filter."
                              data-testid={`pending-outreach-badge-${c.id}`}
                            >
                              <span className="text-amber-600">⏳</span> Pending
                            </span>
                          )}
                          {c.do_not_contact?.flagged && (
                            <span className="text-[8px] font-semibold uppercase tracking-wide text-red-700 bg-red-50 border border-red-300 px-1 py-0 rounded flex items-center gap-0.5"
                              title={c.do_not_contact?.reason || 'Do Not Contact'}
                              data-testid={`dnc-badge-${c.id}`}>
                              <span className="text-red-500">⊘</span> DNC
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-2 align-top">
                        {companyAssets.length > 0 ? (
                          <div className="space-y-0.5">
                            {companyAssets.slice(0, 3).map(a => {
                              const curStatus = a.emailing_status || 'Not Started';
                              return (
                              <div key={a.id} className="flex items-center gap-1 text-[10px] overflow-hidden">
                                <span className="font-medium text-slate-700 truncate" style={{maxWidth:'100px'}}>{a.asset_name}</span>
                                <Badge variant="secondary" className={`text-[8px] px-1 py-0 shrink-0 ${STAGE_COLORS[a.stage] || 'bg-slate-100 text-slate-600'}`}>{a.stage}</Badge>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={(e) => e.stopPropagation()}
                                      className={`text-[8px] px-1 py-0 shrink-0 rounded font-medium hover:ring-1 hover:ring-slate-300 transition ${STATUS_COLORS[curStatus] || 'bg-slate-100 text-slate-500'}`}
                                      title="Click to change emailing status"
                                      data-testid={`asset-emailing-status-${a.id}`}
                                    >
                                      {curStatus === 'Not Started' ? 'Not Emailed' : curStatus}
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    align="start" className="w-44 p-1"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`asset-emailing-status-popover-${a.id}`}
                                  >
                                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100 mb-1">
                                      Emailing Status
                                    </div>
                                    {['Not Started', 'In Progress', 'Completed'].map(s => (
                                      <button
                                        key={s}
                                        onClick={(e) => { e.stopPropagation(); handleEmailingStatusChange(a.id, s); }}
                                        className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-slate-50 flex items-center gap-2 ${s === curStatus ? 'bg-slate-50 font-medium' : ''}`}
                                        data-testid={`asset-emailing-status-opt-${a.id}-${s.replace(/\s/g, '-')}`}
                                      >
                                        <span className={`inline-block w-2 h-2 rounded-full ${STATUS_COLORS[s]?.split(' ')[0] || 'bg-slate-300'}`} />
                                        {s === 'Not Started' ? 'Not Emailed' : s}
                                        {s === curStatus && <span className="ml-auto text-emerald-600 text-xs">✓</span>}
                                      </button>
                                    ))}
                                  </PopoverContent>
                                </Popover>
                              </div>
                              );
                            })}
                            {companyAssets.length > 3 && <span className="text-[9px] text-slate-400">+{companyAssets.length - 3} more</span>}
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-2 text-left align-top">
                        <Badge variant={c.ncd_status === 'Not Received' ? 'outline' : 'secondary'}
                          className={`text-[9px] px-1.5 py-0 ${c.ncd_status === 'Received' || c.ncd_status === 'Shared with Pharma' || c.ncd_status === 'Shared with Investors' ? 'bg-sky-100 text-sky-700' : ''}`}>
                          {c.ncd_status || 'Not Received'}
                        </Badge>
                      </td>
                      <td className="py-2 text-left align-top">
                        <Badge variant="secondary" className={`text-[9px] px-1.5 py-0 ${AGREEMENT_STATUS_COLORS[c.partnership_status || 'Not Started'] || 'bg-slate-100 text-slate-600'}`}>
                          {c.partnership_status || 'Not Started'}
                        </Badge>
                      </td>
                      <td className="py-2 text-left align-top">
                        <div className="flex items-center gap-2">
                          <button onClick={(e) => { e.stopPropagation(); handleEdit(c); }} className="p-1 rounded hover:bg-sky-50 text-sky-500" title="Edit" data-testid={`edit-biotech-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={(e) => { e.stopPropagation(); handleShareStatus(c.id); }} className="p-1 rounded hover:bg-violet-50 text-violet-500" title="Share" data-testid={`share-biotech-${c.id}`}><Share2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => { fetchOutreach(c.id); openOutreachDialog(c.id); }} className="p-1 rounded hover:bg-emerald-50 text-emerald-600" title="Outreach" data-testid={`add-outreach-${c.id}`}><Send className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setDeleteTarget(c)} className="p-1 rounded hover:bg-red-50 text-red-500" title="Delete" data-testid={`delete-biotech-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={currentPage} pages={totalPages} total={filtered.length !== companies.length ? filtered.length : totalCount} onPageChange={handlePageChange} />
          {filtered.length !== companies.length && (
            <p className="text-[10px] text-slate-400 text-center mt-1">Showing {filtered.length} of {totalCount} (filtered client-side)</p>
          )}
        </Card>
      )}

      {/* Outreach Dialog */}
      <Dialog open={outreachDialogOpen} onOpenChange={(open) => { setOutreachDialogOpen(open); if (!open) { setEditingOutreach(null); setOutreachForm({ ...emptyOutreachForm }); setOutreachMode('existing'); setOutreachAssets([]); setContactSearchTerm(''); setContactDropdownOpen(false); setOutreachSelectedIds([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingOutreach ? 'Edit Outreach' : 'Add Outreach Contact'}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Tag a pharma or investor contact to this biotech company.</DialogDescription>
          </DialogHeader>

          {!editingOutreach && (
            <div className="flex bg-slate-100 rounded-md p-0.5 mb-2">
              <button type="button" onClick={() => {
                // Switching to "From Existing Contacts" — wipe any manual
                // contact fields the user might have typed and reset the
                // multi-select bucket, but PRESERVE the linked asset, status,
                // and contact_type so the user doesn't lose context when
                // toggling tabs (2026-06-06 user report).
                setOutreachMode('existing');
                setOutreachForm(p => ({
                  ...emptyOutreachForm,
                  biotech_company_id: p.biotech_company_id,
                  asset_id: p.asset_id, asset_name: p.asset_name,
                  status: p.status, status_notes: p.status_notes,
                  contact_type: p.contact_type || 'Pharma',
                }));
                setOutreachSelectedIds([]);
              }}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors ${outreachMode === 'existing' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                data-testid="outreach-mode-existing">
                From Existing Contacts
              </button>
              <button type="button" onClick={() => {
                // Switching to "Add Manually" — clear contact fields that
                // may have been mirrored in by toggleExistingContact so the
                // user starts from a blank slate (the exact bug the user
                // reported: "whatever I select from the already available,
                // it makes a copy in the manual one"). Same asset / status
                // preservation as the other tab.
                setOutreachMode('manual');
                setOutreachForm(p => ({
                  ...emptyOutreachForm,
                  biotech_company_id: p.biotech_company_id,
                  asset_id: p.asset_id, asset_name: p.asset_name,
                  status: p.status, status_notes: p.status_notes,
                  contact_type: p.contact_type || 'Pharma',
                }));
                setOutreachSelectedIds([]);
              }}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors ${outreachMode === 'manual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                data-testid="outreach-mode-manual">
                Add Manually
              </button>
            </div>
          )}

          <form onSubmit={handleOutreachSubmit} className="space-y-4 mt-1">
            {outreachMode === 'existing' && !editingOutreach && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Contact Type</Label>
                  <Select value={outreachForm.contact_type} onValueChange={v => { setOutreachForm(p => ({ ...emptyOutreachForm, biotech_company_id: p.biotech_company_id, contact_type: v })); setOutreachSelectedIds([]); }}>
                    <SelectTrigger data-testid="select-outreach-source-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pharma">Pharma Contact</SelectItem>
                      <SelectItem value="Investor">Investor</SelectItem>
                      <SelectItem value="Biotech">Biotech (In-licenser)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Select {outreachForm.contact_type === 'Pharma' ? 'Pharma Contact' : outreachForm.contact_type === 'Biotech' ? 'Biotech Contact' : 'Investor'}</Label>
                  <div className="relative" data-testid="select-existing-contact">
                    <Input
                      placeholder={`Search ${outreachForm.contact_type === 'Pharma' ? 'pharma contacts' : outreachForm.contact_type === 'Biotech' ? 'biotech contacts' : 'investors'}...`}
                      value={contactSearchTerm}
                      onChange={e => { setContactSearchTerm(e.target.value); setContactDropdownOpen(true); }}
                      onFocus={() => setContactDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setContactDropdownOpen(false), 200)}
                      className="h-9 text-sm"
                    />
                    {contactDropdownOpen && (() => {
                      const pool = outreachForm.contact_type === 'Pharma' ? pharmaContacts : outreachForm.contact_type === 'Biotech' ? biotechContactsList : investorContacts;
                      const allContacts = outreachForm.contact_type === 'Investor'
                        ? [...pool].sort((a, b) => (a.firm_name || a.name || '').localeCompare(b.firm_name || b.name || ''))
                        : [...pool].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                      const term = contactSearchTerm.toLowerCase().trim();
                      const filtered = term
                        ? allContacts.filter(c =>
                            (c.name || '').toLowerCase().includes(term) ||
                            (c.firm_name || '').toLowerCase().includes(term) ||
                            (c.company_name || '').toLowerCase().includes(term) ||
                            (c.contact_person || '').toLowerCase().includes(term) ||
                            (c.email || '').toLowerCase().includes(term) ||
                            (c.designation || '').toLowerCase().includes(term)
                          )
                        : allContacts.slice(0, 200);
                      if (filtered.length === 0) {
                        return (
                          <div className="absolute z-[9999] w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-4 text-center">
                            <p className="text-sm text-slate-400">{term ? `No contacts found for "${contactSearchTerm}"` : 'No contacts available.'}</p>
                          </div>
                        );
                      }
                      return (
                        <div className="absolute z-[9999] w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl"
                          onMouseDown={e => e.preventDefault()}>
                          {term && (
                            <p className="text-[10px] text-slate-400 px-3 py-1.5 border-b border-slate-100 bg-slate-50 rounded-t-lg">
                              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                            </p>
                          )}
                          <div className="max-h-[220px] overflow-y-auto">
                          {filtered.slice(0, 200).map(c => {
                            const isSelected = outreachSelectedIds.includes(c.id);
                            return (
                            <button
                              key={c.id}
                              type="button"
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-sky-50 flex items-center gap-2 ${isSelected ? 'bg-sky-50' : ''}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                toggleExistingContact(c.id, outreachForm.contact_type);
                                // Keep dropdown open so the user can pick more
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                                className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 shrink-0 pointer-events-none"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">
                                  {outreachForm.contact_type === 'Investor' ? (c.firm_name || c.name) : c.name}
                                </p>
                                <p className="text-xs text-slate-400 truncate">
                                  {outreachForm.contact_type !== 'Investor'
                                    ? `${c.company_name || ''} ${c.email ? '· ' + c.email : ''}`
                                    : `${c.contact_person ? c.contact_person + ' · ' : ''}${c.email || ''}`
                                  }
                                </p>
                              </div>
                            </button>
                            );
                          })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {outreachSelectedIds.length > 0 && (
                    <div className="bg-sky-50 rounded-md p-3 border border-sky-100 space-y-1.5" data-testid="outreach-selected-chips">
                      <p className="text-[10px] font-semibold text-sky-700 uppercase tracking-wide">{outreachSelectedIds.length} contact{outreachSelectedIds.length > 1 ? 's' : ''} selected</p>
                      <div className="flex flex-wrap gap-1.5">
                        {outreachSelectedIds.map(cid => {
                          const list = outreachForm.contact_type === 'Pharma' ? pharmaContacts : outreachForm.contact_type === 'Biotech' ? biotechContactsList : investorContacts;
                          const c = list.find(x => x.id === cid);
                          if (!c) return null;
                          const display = outreachForm.contact_type !== 'Investor'
                            ? (c.name || c.email)
                            : (c.contact_person || c.firm_name || c.name);
                          return (
                            <span key={cid} className="inline-flex items-center gap-1 bg-white border border-sky-200 rounded-full px-2 py-0.5 text-[11px] text-sky-700">
                              {display}
                              <button
                                type="button"
                                className="hover:text-rose-600"
                                onClick={() => toggleExistingContact(cid, outreachForm.contact_type)}
                                aria-label={`Remove ${display}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(outreachMode === 'manual' || editingOutreach) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input value={outreachForm.contact_name} onChange={e => setOutreachForm(p => ({ ...p, contact_name: e.target.value }))} required placeholder="e.g. John Smith" data-testid="input-outreach-name" />
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input value={outreachForm.designation} onChange={e => setOutreachForm(p => ({ ...p, designation: e.target.value }))} placeholder="e.g. Head of BD" data-testid="input-outreach-designation" />
                </div>
                <div className="space-y-2">
                  <Label>Company *</Label>
                  <CompanySelect
                    value={outreachForm.company_name}
                    onChange={v => setOutreachForm(p => ({ ...p, company_name: v }))}
                    onPickRich={({ name, country }) => setOutreachForm(p => ({
                      ...p,
                      company_name: name,
                      country: (country && !p.country) ? country : p.country,
                    }))}
                    placeholder="Select pharma company"
                    testId="input-outreach-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={outreachForm.contact_email} onChange={e => setOutreachForm(p => ({ ...p, contact_email: e.target.value }))} data-testid="input-outreach-email" />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <CountrySelect value={outreachForm.country} onChange={v => setOutreachForm(p => ({ ...p, country: v }))} testId="input-outreach-country" />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={outreachForm.contact_type} onValueChange={v => setOutreachForm(p => ({ ...p, contact_type: v }))}>
                    <SelectTrigger data-testid="select-outreach-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pharma">Pharma</SelectItem>
                      <SelectItem value="Investor">Investor</SelectItem>
                      <SelectItem value="Biotech">Biotech (In-licenser)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Asset Selector — always show so the user can confirm which
                asset the outreach is being tagged against. Previously this
                was hidden when the biotech had only 1 asset (auto-selected),
                which hid the asset context entirely in the dialog. 2026-06-06
                user report: "from the biotechs list page outreach dialog
                doesn't show the asset details." */}
            {outreachAssets.length === 0 ? (
              <div className="space-y-2">
                <Label>Linked Asset / Molecule</Label>
                <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="outreach-no-assets">
                  No assets on file for this biotech. Outreach will be saved without an asset link.
                </div>
              </div>
            ) : outreachAssets.length === 1 ? (
              <div className="space-y-2">
                <Label>Linked Asset / Molecule</Label>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="outreach-single-asset">
                  <span className="font-medium">{outreachAssets[0].asset_name}</span>
                  {outreachAssets[0].phase && <span className="text-slate-500"> · {outreachAssets[0].phase}</span>}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Linked Asset / Molecule</Label>
                <Select value={outreachForm.asset_id || '_none_'} onValueChange={v => {
                  if (v === '_none_') {
                    setOutreachForm(p => ({ ...p, asset_id: '', asset_name: '' }));
                  } else {
                    const a = outreachAssets.find(a => a.id === v);
                    setOutreachForm(p => ({ ...p, asset_id: v, asset_name: a?.asset_name || '' }));
                  }
                }}>
                  <SelectTrigger data-testid="outreach-asset-select"><SelectValue placeholder="Select an asset..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">No specific asset</SelectItem>
                    {outreachAssets.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.asset_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={outreachForm.status} onValueChange={v => setOutreachForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="select-outreach-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OUTREACH_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status Note <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea value={outreachForm.status_notes || ''} onChange={e => setOutreachForm(p => ({ ...p, status_notes: e.target.value }))} rows={1} placeholder="Add a note for this status..." data-testid="input-outreach-status-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setOutreachDialogOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                className="bg-slate-900 text-white hover:bg-slate-800"
                disabled={!editingOutreach && outreachMode === 'existing' ? outreachSelectedIds.length === 0 : !outreachForm.contact_name}
                data-testid="submit-outreach-btn"
              >
                {editingOutreach
                  ? 'Update'
                  : (outreachMode === 'existing' && outreachSelectedIds.length > 1
                      ? `Add ${outreachSelectedIds.length} Outreach`
                      : 'Add Outreach')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold text-slate-700">{deleteTarget?.company_name}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Document Confirmation */}
      <AlertDialog open={!!deleteDocTarget} onOpenChange={(open) => !open && setDeleteDocTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Remove Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <span className="font-semibold text-slate-700">{deleteDocTarget?.original_filename}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-doc-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteDoc(deleteDocTarget?.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-doc-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Email Floating Action Bar */}
      {selectedCompanies.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-lg shadow-xl px-5 py-3 flex items-center gap-3 animate-in slide-in-from-bottom-4" data-testid="bulk-email-action-bar">
          <span className="text-sm font-medium">{selectedCompanies.size} compan{selectedCompanies.size !== 1 ? 'ies' : 'y'} selected</span>
          <Button
            size="sm"
            className="bg-teal-500 text-white hover:bg-teal-600 h-8 text-xs font-semibold"
            onClick={() => setAiChatOpen(true)}
            data-testid="ai-email-open-btn"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Personalized Email
          </Button>
          <Button
            size="sm"
            className="bg-white text-slate-900 hover:bg-slate-100 h-8 text-xs font-semibold"
            onClick={openBulkEmail}
            data-testid="bulk-email-open-btn"
          >
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Same Email to All
          </Button>
          <button onClick={() => setSelectedCompanies(new Set())} className="text-slate-400 hover:text-white text-xs ml-1" data-testid="bulk-email-clear-btn">Clear</button>
        </div>
      )}

      {/* AI Email Chat Panel */}
      <BulkEmailChat
        isOpen={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        selectedCompanyIds={selectedCompanies}
        stageFilter={stageFilter}
        onSendComplete={() => {
          setAiChatOpen(false);
          setSelectedCompanies(new Set());
        }}
      />

      {/* Bulk Email Compose Dialog (Same Email to All) */}
      <Dialog open={bulkEmailOpen} onOpenChange={(open) => { if (!open && !bulkEmailSending) { setBulkEmailOpen(false); setBulkEmailPreview(null); setBulkEmailMatchedAssets(null); } }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Same Email to All</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Send the same email to {selectedCompanies.size} selected compan{selectedCompanies.size !== 1 ? 'ies' : 'y'}.
            </DialogDescription>
          </DialogHeader>

          {bulkEmailPreview && (
            <div className="bg-slate-50 rounded-md p-3">
              <p className="text-xs font-semibold text-slate-700 mb-1">
                {bulkEmailPreview.total} recipient{bulkEmailPreview.total !== 1 ? 's' : ''} from {bulkEmailPreview.companies} compan{bulkEmailPreview.companies !== 1 ? 'ies' : 'y'}
                {bulkEmailPreview.skipped_companies > 0 && (
                  <span className="text-slate-500 font-normal ml-1">
                    ({bulkEmailPreview.skipped_companies} skipped
                    {bulkEmailPreview.include_previously_contacted ? ' — contacted in last 24h' : ' — already contacted before'})
                  </span>
                )}
              </p>
              <div className="max-h-24 overflow-y-auto space-y-0.5">
                {bulkEmailPreview.recipients?.slice(0, 20).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px]">
                    <span className="font-medium text-slate-700">{r.contact_name || '—'}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{r.contact_email}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-400 truncate max-w-[120px]">{r.company_name}</span>
                  </div>
                ))}
                {(bulkEmailPreview.recipients?.length || 0) > 20 && (
                  <p className="text-[10px] text-slate-400">...and {bulkEmailPreview.total - 20} more</p>
                )}
              </div>
              {bulkEmailPreview.skipped_companies > 0 && !bulkEmailIncludePrevContacted && (
                <div className="mt-2 p-2.5 rounded bg-amber-50 border border-amber-300 text-[11px] text-amber-900 flex items-start gap-2" data-testid="bulk-email-skipped-banner">
                  <span className="text-amber-600 text-base leading-none mt-0.5">⚠</span>
                  <div className="flex-1">
                    <p className="font-semibold mb-0.5">{bulkEmailPreview.skipped_companies} of {bulkEmailPreview.companies} companies are being skipped because they were contacted before.</p>
                    <p className="mb-2">
                      Re-engage them with a fresh message — anyone emailed in the last 24h stays auto-protected, so your most recent batch won't be re-contacted.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setBulkEmailIncludePrevContacted(true)}
                      className="h-7 px-3 text-[11px] bg-amber-600 text-white hover:bg-amber-700 gap-1.5"
                      data-testid="bulk-email-include-prev-inline-btn"
                    >
                      <Mail className="w-3 h-3" /> Include all {bulkEmailPreview.companies} — re-engage already-contacted
                    </Button>
                  </div>
                </div>
              )}
              {bulkEmailPreview.skipped_companies > 0 && bulkEmailIncludePrevContacted && (
                <div className="mt-2 p-2 rounded bg-sky-50 border border-sky-300 text-[11px] text-sky-900 flex items-center justify-between gap-2" data-testid="bulk-email-included-banner">
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-sky-600 text-white flex items-center justify-center text-[10px]">✓</span>
                    Re-engaging <span className="font-semibold">all {bulkEmailPreview.companies} companies</span> (last-24h safety guard still active).
                  </span>
                  <button
                    type="button"
                    onClick={() => setBulkEmailIncludePrevContacted(false)}
                    className="text-sky-700 hover:underline text-[11px]"
                    data-testid="bulk-email-revert-include-btn"
                  >
                    Undo
                  </button>
                </div>
              )}
              {bulkEmailPreview.total === 0 && bulkEmailIncludePrevContacted && (
                <p className="text-xs text-amber-600 mt-1">
                  All selected companies were contacted in the last 24 hours. Try a different selection or wait 24h.
                </p>
              )}
            </div>
          )}

          <div className="space-y-4 mt-1">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Subject *</Label>
              <Input
                value={bulkEmailSubject}
                onChange={e => setBulkEmailSubject(e.target.value)}
                placeholder="e.g. Partnership Opportunity — ForesightRx"
                data-testid="bulk-email-subject"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Email Body *</Label>
              <Textarea
                value={bulkEmailBody}
                onChange={e => setBulkEmailBody(e.target.value)}
                rows={10}
                placeholder={"Dear [Name],\n\nI hope this message finds you well...\n\nBest regards,\nAmit Dang"}
                className="text-sm"
                data-testid="bulk-email-body"
              />
              <p className="text-[10px] text-slate-500 leading-relaxed">
                <span className="font-semibold text-slate-700">Per-recipient placeholders (optional):</span>
                {' '}
                <code className="bg-slate-100 px-1 rounded">[Name]</code>{' '}
                <code className="bg-slate-100 px-1 rounded">[Asset]</code>{' '}
                <code className="bg-slate-100 px-1 rounded">[Indication]</code>{' '}
                <code className="bg-slate-100 px-1 rounded">[Modality]</code>{' '}
                <code className="bg-slate-100 px-1 rounded">[Stage]</code>{' '}
                <code className="bg-slate-100 px-1 rounded">[Company]</code>
                <br/>
                Compound token <code className="bg-slate-100 px-1 rounded">[indication / mechanism / phase]</code> also supported.
                Each is auto-filled from the company's most-advanced asset. Empty fields gracefully fall back to generic phrasing.
              </p>
            </div>
            <div className="flex items-center gap-2 pt-1 px-0.5">
              <input
                type="checkbox"
                id="bulk-email-include-prev"
                checked={bulkEmailIncludePrevContacted}
                onChange={(e) => setBulkEmailIncludePrevContacted(e.target.checked)}
                disabled={bulkEmailSending}
                className="h-3.5 w-3.5 rounded border-slate-300"
                data-testid="bulk-email-include-prev-contacted"
              />
              <label htmlFor="bulk-email-include-prev" className="text-xs text-slate-600 select-none cursor-pointer">
                Include companies already contacted in prior outreach
                <span className="text-slate-400 ml-1">(anyone emailed in the last 24h is still skipped as a safety guard)</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setBulkEmailOpen(false); setBulkEmailPreview(null); setBulkEmailMatchedAssets(null); }} disabled={bulkEmailSending} data-testid="bulk-email-cancel-btn">
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={sendTestEmail}
                disabled={bulkEmailSending || !bulkEmailSubject.trim() || !bulkEmailBody.trim() || (bulkEmailPreview && bulkEmailPreview.total === 0)}
                className="gap-1.5"
                title="Send 3 personalized preview emails to your own inbox before firing the real batch"
                data-testid="bulk-email-test-btn"
              >
                <Mail className="w-3.5 h-3.5" /> Send test to me
              </Button>
              <Button
                onClick={() => setReadinessOpen(true)}
                disabled={bulkEmailSending || !bulkEmailSubject.trim() || !bulkEmailBody.trim() || (bulkEmailPreview && bulkEmailPreview.total === 0)}
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="bulk-email-send-btn"
              >
                {bulkEmailSending ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending...</>
                ) : (
                  <><Mail className="w-3.5 h-3.5 mr-1.5" /> Review & send · {bulkEmailPreview?.total || '...'} contacts</>
                )}
              </Button>
            </div>

            {bulkEmailProgress && (
              <div className="mt-3">
                <BulkSendProgress
                  progress={bulkEmailProgress}
                  testId="bulk-email-progress"
                  onDone={() => { setBulkEmailProgress(null); setBulkEmailOpen(false); }}
                />
                {bulkEmailProgress.currentName && bulkEmailProgress.status === 'running' && (
                  <p className="text-[11px] text-slate-500 truncate flex items-center gap-1.5 mt-2 px-1">
                    <Loader2 className="w-3 h-3 animate-spin text-sky-500" />
                    <span>Sending to <span className="font-medium text-slate-700">{bulkEmailProgress.currentName}</span>…</span>
                  </p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign Readiness confirmation dialog — final gut-check before
          firing a large blast. Locks the user out of accidental send by
          requiring an explicit confirm on a summary of recipients / cost
          / duration / cohort. Never appears if the recipient count is 0. */}
      <Dialog open={readinessOpen} onOpenChange={setReadinessOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="campaign-readiness-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Mail className="w-5 h-5 text-teal-600" />
              Campaign Readiness Check
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const recipients = bulkEmailPreview?.total || 0;
            // Gmail API sustained rate ≈ 1 email every ~2.5s under quota
            // (batched at ~500/day soft limit). Estimate duration in
            // minutes so the user sees when the blast will finish.
            const durSec = recipients * 2.5;
            const durHum = durSec < 90
              ? `${Math.round(durSec)}s`
              : durSec < 3600
                ? `${Math.round(durSec / 60)} min`
                : `${(durSec / 3600).toFixed(1)} hrs`;
            // AI cost estimate: only when matched_assets is empty (each
            // recipient triggers a fresh Claude call ≈ $0.003). If
            // matched_assets is prebuilt (i.e. user pre-generated via the
            // Composer) send-time cost drops to $0.
            const aiCost = (bulkEmailMatchedAssets && Object.keys(bulkEmailMatchedAssets).length > 0)
              ? 0
              : recipients * 0.003;
            const skipped = bulkEmailPreview?.skipped_companies || 0;
            const dailyCap = 500;
            const willBeCapped = recipients > dailyCap;
            return (
              <div className="space-y-3" data-testid="readiness-content">
                {/* Cohort context */}
                {cohortFilter && (
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                    Cohort · <span className="text-slate-800 normal-case font-medium">{cohortFilter.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  </div>
                )}
                {/* Headline stat: recipient count */}
                <div className="rounded-lg border border-teal-200 bg-teal-50 p-3.5">
                  <div className="text-[10px] uppercase tracking-wide text-teal-600 font-semibold">Estimated campaign size</div>
                  <div className="text-3xl font-bold text-teal-900 leading-tight mt-0.5" data-testid="readiness-recipients">
                    {recipients.toLocaleString()} <span className="text-sm font-normal text-teal-700">recipients</span>
                  </div>
                </div>
                {/* Grid summary */}
                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <div className="rounded-md border border-slate-200 p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Ready to receive</div>
                    <div className="font-bold text-emerald-700 text-lg" data-testid="readiness-ready">{recipients.toLocaleString()}</div>
                  </div>
                  <div className="rounded-md border border-slate-200 p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Skipped (already contacted)</div>
                    <div className="font-bold text-slate-700 text-lg" data-testid="readiness-skipped">{skipped.toLocaleString()}</div>
                  </div>
                  <div className="rounded-md border border-slate-200 p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Est. duration</div>
                    <div className="font-bold text-sky-700 text-lg" data-testid="readiness-duration">{durHum}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Gmail rate ≈ 1 / 2.5s</div>
                  </div>
                  <div className="rounded-md border border-slate-200 p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Est. AI cost</div>
                    <div className="font-bold text-violet-700 text-lg" data-testid="readiness-cost">
                      {aiCost === 0 ? 'Free' : `$${aiCost.toFixed(2)}`}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {aiCost === 0 ? 'Templates pre-generated' : 'Claude per-email personalisation'}
                    </div>
                  </div>
                </div>
                {/* Daily cap warning */}
                {willBeCapped && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] text-amber-800" data-testid="readiness-cap-warning">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">Above Gmail's soft daily cap ({dailyCap}/day)</div>
                      Gmail may throttle after ~{dailyCap} emails. Consider pacing this across {Math.ceil(recipients / dailyCap)} days using the Bulk Scheduler (coming soon).
                    </div>
                  </div>
                )}
                {/* Preflight — recipients preview */}
                {bulkEmailPreview?.recipients && bulkEmailPreview.recipients.length > 0 && (
                  <details className="text-[11px] text-slate-600">
                    <summary className="cursor-pointer font-semibold text-slate-700 hover:text-slate-900">
                      Preview first 5 recipients
                    </summary>
                    <ul className="mt-1.5 space-y-0.5 max-h-24 overflow-y-auto">
                      {bulkEmailPreview.recipients.slice(0, 5).map((r, i) => (
                        <li key={i} className="truncate">
                          <span className="font-medium">{r.name || r.email}</span>
                          <span className="text-slate-400"> · {r.email}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })()}
          <DialogFooter className="mt-3 flex justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => setReadinessOpen(false)}
              disabled={bulkEmailSending}
              data-testid="readiness-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => { setReadinessOpen(false); sendBulkEmail(); }}
              disabled={bulkEmailSending || !bulkEmailPreview?.total}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="readiness-confirm-btn"
            >
              {bulkEmailSending
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Launching…</>
                : <><Mail className="w-3.5 h-3.5 mr-1.5" /> Launch campaign · {(bulkEmailPreview?.total || 0).toLocaleString()} emails</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Non-Target Entity Scanner Dialog */}
      <Dialog open={cdmoScanOpen} onOpenChange={setCdmoScanOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-500" /> Database Cleanup
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Flagged entities that don't belong in your CRM — CDMOs, CROs, IT firms, consultancies, legal, financial services, recruiters, marketing agencies, and other service providers. Scans both Biotech and Pharma collections.
            </DialogDescription>
          </DialogHeader>

          {cdmoScanning ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
              <span className="text-sm text-slate-500">Scanning all companies across Biotech & Pharma...</span>
            </div>
          ) : cdmoFlagged.length === 0 ? (
            <div className="text-center py-12">
              <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-medium">Database is clean</p>
              <p className="text-xs text-slate-400 mt-1">No non-target entities detected across Biotech or Pharma</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">{cdmoFlagged.length} non-target entit{cdmoFlagged.length === 1 ? 'y' : 'ies'} found across Biotech & Pharma</p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => {
                    if (cdmoSelected.size === cdmoFlagged.length) setCdmoSelected(new Set());
                    else setCdmoSelected(new Set(cdmoFlagged.map(f => f.id)));
                  }} data-testid="cdmo-select-all">
                    {cdmoSelected.size === cdmoFlagged.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  {cdmoSelected.size > 0 && (
                    <Button variant="destructive" size="sm" className="text-xs h-7" onClick={removeFlaggedCdmos} disabled={cdmoRemoving} data-testid="cdmo-remove-selected">
                      {cdmoRemoving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                      Remove {cdmoSelected.size} Selected
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {cdmoFlagged.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${cdmoSelected.has(item.id) ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100 hover:bg-slate-50'}`}
                    onClick={() => setCdmoSelected(prev => {
                      const next = new Set(prev);
                      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                      return next;
                    })}
                    data-testid={`cdmo-item-${item.id}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 shrink-0 transition-colors ${cdmoSelected.has(item.id) ? 'bg-red-500 border-red-500' : 'border-slate-300'}`}>
                      {cdmoSelected.has(item.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-slate-800">{item.company_name}</span>
                        <Badge variant="secondary" className="text-[9px] px-1.5 py-0">{item.collection === 'pharma_contacts' ? 'Pharma' : 'Biotech'}</Badge>
                        {item.source && item.source !== 'manual' && (
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0">{item.source}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-amber-600 font-medium mb-1">Matched: {item.reason}</p>
                      <p className="text-xs text-slate-500 line-clamp-2">{item.snippet}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk enrichment results dialog */}
      <Dialog open={!!bulkEnrichResult} onOpenChange={(open) => { if (!open) setBulkEnrichResult(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-enrich-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Sparkles className="w-5 h-5 text-emerald-600" />
              Conference Enrichment Summary
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Non-destructive merge — no existing field was overwritten. Empty fields filled, new assets & contacts appended.
            </DialogDescription>
          </DialogHeader>
          {bulkEnrichResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { label: 'Parsed', value: bulkEnrichResult.total_parsed, color: 'bg-slate-50 text-slate-700' },
                  { label: 'Enriched', value: bulkEnrichResult.enriched_existing, color: 'bg-emerald-50 text-emerald-700' },
                  { label: 'New', value: bulkEnrichResult.created_new, color: 'bg-sky-50 text-sky-700' },
                  { label: 'Assets added', value: bulkEnrichResult.assets_added, color: 'bg-violet-50 text-violet-700' },
                  { label: 'Contacts added', value: bulkEnrichResult.contacts_added, color: 'bg-amber-50 text-amber-700' },
                ].map(s => (
                  <div key={s.label} className={`rounded-md px-3 py-2.5 ${s.color}`}>
                    <div className="text-[10px] uppercase tracking-wider font-semibold opacity-75">{s.label}</div>
                    <div className="text-2xl font-bold">{s.value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Per-company detail</p>
                <div className="max-h-[45vh] overflow-y-auto border border-slate-100 rounded-md">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2">Company</th>
                        <th className="px-3 py-2">Action</th>
                        <th className="px-3 py-2">Changes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bulkEnrichResult.details || []).map((d, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2">
                            <div className="font-medium text-slate-800">{d.matched_name || d.parsed_name}</div>
                            {d.matched_name && d.matched_name.toLowerCase() !== (d.parsed_name || '').toLowerCase() && (
                              <div className="text-[10px] text-slate-400">parsed as "{d.parsed_name}" · match {d.match_score}%</div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {d.action === 'enriched' ? (
                              <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">Enriched</Badge>
                            ) : (
                              <Badge variant="outline" className="text-sky-700 border-sky-200 bg-sky-50">New</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {d.fields_updated?.length > 0 && (
                              <span className="mr-2">+{d.fields_updated.length} fields</span>
                            )}
                            {d.assets_added > 0 && <span className="mr-2">+{d.assets_added} assets</span>}
                            {d.assets_enriched > 0 && <span className="mr-2">↺ {d.assets_enriched} assets</span>}
                            {d.contacts_added > 0 && <span className="mr-2">+{d.contacts_added} contacts</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setBulkEnrichResult(null)}
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="bulk-enrich-result-done-btn"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// Inline duplicate-pair row — rendered inside the banner
function DupPairRow({ pair, pairKey, idx, done, merging, selected, onToggleSelect, onMergeClick, onDismissClick }) {
  const [keepSide, setKeepSide] = React.useState('a');
  const survivor = keepSide === 'a' ? pair.a : pair.b;
  const drop = keepSide === 'a' ? pair.b : pair.a;

  if (done) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-800"
        data-testid={`dup-row-${idx}-done`}>
        <Check className="w-3.5 h-3.5" />
        <span>Merged: <strong>{pair.a.company_name}</strong> ↔ <strong>{pair.b.company_name}</strong></span>
      </div>
    );
  }

  return (
    <div className={`bg-white border rounded-md p-2.5 ${selected ? 'border-sky-400 ring-1 ring-sky-200' : 'border-rose-100'}`} data-testid={`dup-row-${idx}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          disabled={merging}
          className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
          data-testid={`dup-select-${idx}`}
          title="Select for bulk action"
        />
        <Badge
          className={pair.match_type === 'exact_normalized'
            ? 'bg-rose-600 text-white text-[10px]'
            : 'bg-amber-100 text-amber-800 text-[10px] border-amber-200'}
        >
          {pair.score}%
        </Badge>
        <span className="text-[10px] text-slate-400 truncate">
          shared: {(pair.shared_brand || []).join(', ') || pair.normalized}
        </span>
      </div>

      <div className="flex items-stretch gap-2">
        {['a', 'b'].map((side) => {
          const c = pair[side];
          const selected = keepSide === side;
          return (
            <div
              key={side}
              onClick={() => setKeepSide(side)}
              className={`flex-1 border rounded-md p-2 cursor-pointer transition-all ${
                selected
                  ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid={`dup-card-${idx}-${side}`}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-xs font-semibold text-slate-800 truncate">{c.company_name}</span>
                {selected && <Badge className="bg-emerald-600 text-white text-[9px] px-1.5 py-0">KEEP</Badge>}
              </div>
              <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                {c.location && <span>📍 {c.location}</span>}
                <span>{(c.contacts || []).length} contact(s)</span>
                <a
                  href={`/biotech/${c.id}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-sky-600 hover:underline inline-flex items-center gap-0.5"
                  data-testid={`dup-open-${c.id}`}
                >
                  open ↗
                </a>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex items-center justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-3 text-xs text-slate-600 border-slate-300 hover:bg-slate-50"
          onClick={onDismissClick}
          disabled={merging}
          title="These are different companies — don't flag this pair again"
          data-testid={`dup-dismiss-btn-${idx}`}
        >
          <X className="w-3 h-3 mr-1" /> Not a duplicate
        </Button>
        <Button
          size="sm"
          className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-3 text-xs"
          onClick={() => onMergeClick(survivor, drop)}
          disabled={merging}
          data-testid={`dup-merge-btn-${idx}`}
        >
          {merging
            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Working…</>
            : `Merge "${drop.company_name}" into "${survivor.company_name}"`}
        </Button>
      </div>
    </div>
  );
}
